import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { API, fileUrl, formatBRL, formatDateTimeBR, formatApiError } from "../lib/api";
import { isNative, openPdfFile } from "../lib/native";
import { useAuth } from "../lib/auth";
import { unitLabelForItem } from "../lib/pricing";
import { AchievementIcon, ICON_KEYS } from "../lib/achievementIcons";
import { toast } from "sonner";
import {
  Shield, MagnifyingGlass, ChartBar, Warning, ListChecks, UserCircle,
  FileArrowDown, X, CheckCircle, Pause, Trash, EnvelopeSimple, Phone,
  IdentificationCard, Coins, Bank, Receipt, Buildings, PlusCircle, MinusCircle, ArrowsClockwise, Lightning,
  TrendUp, Crown, UserMinus, Clock, Prohibit, ArrowCounterClockwise, ArrowLeft, PaperPlaneTilt, Eye, EyeSlash, Gift, MapPin, Users, CaretDown, CaretUp, Wallet, ClipboardText, Key, Timer
} from "@phosphor-icons/react";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: ChartBar },
  { key: "analytics", label: "Crescimento", icon: TrendUp },
  { key: "logs", label: "Logs", icon: ClipboardText },
  { key: "passivo", label: "Passivo", icon: Wallet },
  { key: "disputes", label: "Disputas", icon: Warning },
  { key: "defaulters", label: "Inadimplência", icon: Prohibit },
  { key: "kyc", label: "Validar Docs", icon: IdentificationCard },
  { key: "audit", label: "Histórico de Verificações", icon: Clock },
  { key: "listings", label: "Moderação", icon: ListChecks },
  { key: "users", label: "Usuários", icon: UserCircle },
  { key: "campaigns", label: "Mensagens", icon: PaperPlaneTilt },
  { key: "gamification", label: "Recompensas", icon: Gift },
  { key: "deleted", label: "Contas Excluídas", icon: Trash },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const initialTab = params.get("tab") || "dashboard";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (!loading && !user) {
      nav("/");
    } else if (!loading && user && user.role !== "admin") {
      nav("/perfil");
    }
  }, [loading, user, nav]);

  useEffect(() => {
    setParams({ tab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (loading) return <div className="p-8 text-center text-slate-500">Carregando...</div>;
  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-5" data-testid="admin-panel">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-full bg-[#FF6B35] flex items-center justify-center text-white">
          <Shield size={22} weight="fill" />
        </div>
        <div>
          <h1 className="font-heading font-black text-2xl">Painel de Suporte</h1>
          <p className="text-xs text-slate-500">Mediação, moderação e auditoria</p>
        </div>
      </div>

      <UniversalSearch />

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-4 px-4">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-tab-${key}`}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-full text-xs font-bold border-2 ${tab === key ? "bg-[#004E98] text-white border-[#004E98]" : "bg-white text-slate-700 border-slate-200"}`}
          >
            <Icon size={14} weight="bold" /> {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "logs" && <TransactionLogsTab />}
      {tab === "passivo" && <PassivoTab />}
      {tab === "disputes" && <DisputesTab />}
      {tab === "defaulters" && <DefaultersTab />}
      {tab === "kyc" && <KycTab />}
      {tab === "audit" && <KycAuditTab />}
      {tab === "listings" && <ListingsModerationTab />}
      {tab === "users" && <UsersTab />}
      {tab === "campaigns" && <CampaignsTab />}
      {tab === "gamification" && <GamificationTab />}
      {tab === "deleted" && <DeletedAccountsTab />}
    </div>
  );
}

// ============================================================
// Universal Search
// ============================================================

function UniversalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ users: [], bookings: [], listings: [] });
  const [busy, setBusy] = useState(false);

  const run = async (val) => {
    if (!val || val.trim().length < 2) {
      setResults({ users: [], bookings: [], listings: [] });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get("/admin/search", { params: { q: val } });
      setResults(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="aj-card p-4 mb-4">
      <label className="block">
        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1.5">
          Busca Universal (Nome, Email, CPF, Telefone, ID #AJ-XXXX)
        </div>
        <div className="relative">
          <MagnifyingGlass size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); run(e.target.value); }}
            onFocus={() => setOpen(true)}
            placeholder="Digite para buscar..."
            className="aj-input pl-9"
            data-testid="admin-search-input"
          />
          {q && (
            <button onClick={() => { setQ(""); setResults({ users: [], bookings: [], listings: [] }); }} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X size={16} weight="bold" />
            </button>
          )}
        </div>
      </label>
      {open && (results.users.length > 0 || results.bookings.length > 0 || results.listings.length > 0) && (
        <div className="mt-3 space-y-3" data-testid="admin-search-results">
          {results.users.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Usuários</div>
              <div className="space-y-1">
                {results.users.map((u) => (
                  <Link
                    key={u.id}
                    to={`/admin?tab=users&id=${u.id}`}
                    onClick={() => setOpen(false)}
                    className="block p-2 rounded-lg hover:bg-slate-50 border border-slate-100"
                    data-testid={`admin-search-user-${u.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <UserCircle size={20} weight="bold" className="text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{u.name} <span className="text-[10px] text-slate-400 font-mono">{u.support_id}</span></div>
                        <div className="text-xs text-slate-500 truncate">{u.email} · {u.cpf ? u.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—"}</div>
                      </div>
                      {u.role === "admin" && <span className="aj-badge aj-badge-orange">Admin</span>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {results.listings.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Anúncios</div>
              <div className="space-y-1">
                {results.listings.map((l) => (
                  <Link
                    key={l.id}
                    to={`/itens/${l.id}`}
                    className="block p-2 rounded-lg hover:bg-slate-50 border border-slate-100"
                  >
                    <div className="text-sm font-semibold">{l.title}</div>
                    <div className="text-xs text-slate-500">{formatBRL(l.price_per_day)}/{unitLabelForItem(l)[0]} · {l.city}/{l.uf}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {busy && <div className="mt-2 text-xs text-slate-400">Buscando...</div>}
    </div>
  );
}

// ============================================================
// Dashboard
// ============================================================

function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/metrics").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-center py-8 text-slate-500">Carregando métricas...</div>;

  const cards = [
    { label: "Receita Líquida Total", value: formatBRL(data.total_net_revenue ?? data.platform_gross), icon: Coins, color: "#16A34A", testId: "metric-platform-gross" },
    { label: "Lucro Real (líquido Asaas)", value: formatBRL(data.lucro_real_app ?? 0), icon: TrendUp, color: "#0E9F6E", testId: "metric-lucro-real" },
    { label: "Custo Asaas (real)", value: formatBRL(data.custo_asaas ?? data.gateway_cost ?? 0), icon: MinusCircle, color: "#DC2626", testId: "metric-custo-asaas" },
    { label: "A liberar (locações ativas)", value: formatBRL(data.escrow_pending), icon: Bank, color: "#0EA5E9", testId: "metric-escrow-pending" },
    { label: "Em disputa (sob análise)", value: formatBRL(data.escrow_held), icon: Warning, color: "#DC2626", testId: "metric-escrow-held" },
    { label: "Total Pago a Locadores", value: formatBRL(data.total_paid_out), icon: Receipt, color: "#7C3AED", testId: "metric-total-paid" },
    { label: "GMV (Volume Bruto)", value: formatBRL(data.gmv), icon: ChartBar, color: "#004E98", testId: "metric-gmv" },
    { label: "Disputas Abertas", value: String(data.open_disputes), icon: Warning, color: "#DC2626", testId: "metric-open-disputes" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="aj-card p-4" data-testid={c.testId}>
            <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase font-bold tracking-wider">
              <c.icon size={14} weight="bold" color={c.color} />
              <span className="truncate">{c.label}</span>
            </div>
            <div className="font-heading font-black text-xl md:text-2xl mt-2" style={{ color: c.color }}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue breakdown */}
      <div className="aj-card p-4" data-testid="revenue-breakdown">
        <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
          <Receipt size={16} weight="bold" /> Composição da Receita
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <BreakdownRow label="Comissão Locador (10% ou mín. R$ 2,49)" value={data.locador_fees || 0} color="#16A34A" />
          <BreakdownRow label="Taxa Locatário (R$ 0,50 fixo)" value={data.locatario_fees_fixed || 0} color="#0EA5E9" />
          <BreakdownRow label="Receita Bruta da Plataforma" value={data.platform_gross || 0} color="#004E98" />
          <BreakdownRow label="Antecipação" value={data.advance_revenue || 0} color="#7C3AED" />
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
          <div className="text-xs text-slate-500">Custo real da Asaas (subtraído)</div>
          <div className="font-bold text-red-600" data-testid="breakdown-custo-asaas">- {formatBRL(data.custo_asaas ?? data.gateway_cost ?? 0)}</div>
        </div>
        <div className="mt-1 flex justify-between items-center font-heading font-black">
          <div>Lucro Real AlugaJá</div>
          <div className="text-emerald-700 text-lg" data-testid="breakdown-lucro-real">{formatBRL(data.lucro_real_app ?? data.total_net_revenue ?? 0)}</div>
        </div>
      </div>

      <ProfitAccountCard />

      <div className="aj-card p-4">
        <div className="font-heading font-bold text-sm mb-3">GMV últimos 30 dias</div>
        <BarChart series={data.series_30d || []} />
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, color }) {
  return (
    <div className="border border-slate-200 rounded-xl p-2.5">
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{label}</div>
      <div className="font-heading font-black text-base mt-0.5" style={{ color }}>
        {(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
      </div>
    </div>
  );
}

function ProfitAccountCard() {
  const [st, setSt] = useState(null);
  const [walletInput, setWalletInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "Conta Lucro", email: "", cpf_cnpj: "", company_type: "LIMITED",
    mobile_phone: "", income_value: "50000", address: "", address_number: "",
    province: "", postal_code: "", complement: "",
  });

  const load = () => api.get("/admin/asaas/profit-account").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveWallet = async () => {
    if (!walletInput.trim()) { toast.error("Cole o walletId da Conta Lucro."); return; }
    setSaving(true);
    try {
      await api.put("/admin/asaas/profit-account", { wallet_id: walletInput.trim() });
      toast.success("Conta Lucro configurada! O split será aplicado nas próximas cobranças.");
      setWalletInput("");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const createSub = async () => {
    setSaving(true);
    try {
      const body = { ...form, income_value: parseFloat(form.income_value) || 0 };
      await api.post("/admin/asaas/profit-account", body);
      toast.success("Subconta Conta Lucro criada e configurada!");
      setShowCreate(false);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearWallet = async () => {
    if (!window.confirm("Remover a Conta Lucro? O split do Lucro Real deixará de ser aplicado nas próximas cobranças.")) return;
    setSaving(true);
    try {
      await api.delete("/admin/asaas/profit-account");
      toast.success("Conta Lucro removida.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!st) return null;

  return (
    <div className="aj-card p-4" data-testid="profit-account-card">
      <div className="font-heading font-bold text-sm mb-1 flex items-center gap-2">
        <Bank size={16} weight="bold" color="#0E9F6E" /> Conta Lucro (Split do Lucro Real)
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Subconta Asaas que recebe automaticamente o <strong>Lucro Real</strong> (comissão + taxa − custo Asaas) de cada cobrança, isolando o lucro do dinheiro em trânsito.
      </p>

      {st.configured ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="profit-account-configured">
          <div className="flex items-center gap-2 text-emerald-700 font-bold">
            <CheckCircle size={16} weight="fill" /> Conta Lucro ativa
          </div>
          <div className="mt-1 text-slate-600">walletId: <span className="font-mono break-all">{st.wallet_id}</span></div>
          {st.name && <div className="text-slate-600">Nome: {st.name}</div>}
          <div className="text-slate-500 mt-1">Fonte: {st.source === "env" ? "variável de ambiente" : "configuração manual"}</div>
          {st.source !== "env" && (
            <button
              data-testid="profit-wallet-remove"
              onClick={clearWallet} disabled={saving}
              className="mt-2 text-red-600 font-bold underline disabled:opacity-50"
            >
              Remover Conta Lucro
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs space-y-3" data-testid="profit-account-not-configured">
          <div className="flex items-center gap-2 text-amber-700 font-bold">
            <Warning size={16} weight="fill" /> Conta Lucro ainda não configurada
          </div>
          <p className="text-slate-600">
            Sua conta principal Asaas é <strong>{st.main_person_type === "JURIDICA" ? "PJ (CNPJ)" : "PF (CPF)"}</strong>.
            {st.main_person_type !== "JURIDICA" && " Contas CPF não podem criar subcontas — use uma 2ª conta Asaas (ou migre para CNPJ) e cole o walletId dela abaixo."}
          </p>

          <div className="space-y-2">
            <label className="font-bold text-slate-600">Colar walletId da Conta Lucro</label>
            <input
              data-testid="profit-wallet-input"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono"
              placeholder="ex: c0c1688f-636b-42c0-b6ee-7339182276b7"
              value={walletInput}
              onChange={(e) => setWalletInput(e.target.value)}
            />
            <button
              data-testid="profit-wallet-save"
              onClick={saveWallet} disabled={saving}
              className="w-full bg-[#004E98] text-white rounded-lg py-2 font-bold disabled:opacity-50"
            >
              {saving ? "Salvando..." : "Salvar walletId"}
            </button>
          </div>

          {st.can_create_subaccount && (
            <div className="pt-2 border-t border-amber-200">
              <button
                data-testid="profit-create-toggle"
                onClick={() => setShowCreate((v) => !v)}
                className="text-[#004E98] font-bold underline"
              >
                {showCreate ? "Cancelar criação automática" : "Criar subconta automaticamente (via API)"}
              </button>
              {showCreate && (
                <div className="grid grid-cols-2 gap-2 mt-2" data-testid="profit-create-form">
                  {[
                    ["name", "Nome"], ["email", "E-mail"], ["cpf_cnpj", "CNPJ"],
                    ["mobile_phone", "Celular"], ["income_value", "Faturamento mensal"],
                    ["postal_code", "CEP"], ["address", "Endereço"], ["address_number", "Número"],
                    ["province", "Bairro"], ["complement", "Complemento"],
                  ].map(([k, label]) => (
                    <input
                      key={k}
                      data-testid={`profit-create-${k}`}
                      className="border border-slate-300 rounded-lg px-2 py-1.5"
                      placeholder={label}
                      value={form[k]}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                    />
                  ))}
                  <button
                    data-testid="profit-create-submit"
                    onClick={createSub} disabled={saving}
                    className="col-span-2 bg-emerald-600 text-white rounded-lg py-2 font-bold disabled:opacity-50"
                  >
                    {saving ? "Criando..." : "Criar Conta Lucro"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function BarChart({ series }) {
  const max = Math.max(1, ...series.map((s) => s.gmv));
  return (
    <div className="flex items-end gap-1 h-32" data-testid="metrics-chart">
      {series.map((s) => {
        const h = (s.gmv / max) * 100;
        return (
          <div key={s.date} className="flex-1 flex flex-col items-center justify-end" title={`${s.date}: ${s.gmv.toFixed(2)} (${s.count})`}>
            <div
              className="w-full bg-gradient-to-t from-[#004E98] to-[#0EA5E9] rounded-t"
              style={{ height: `${h}%`, minHeight: s.gmv > 0 ? 2 : 0 }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Disputes
// ============================================================

// V17.9 — Painel admin para contestações de caução
function AdminCaucaoClaimsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/caucao-claims", { params: { status: "pending_admin" } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resolve = async (side) => {
    if (!selected) return;
    const payload = { side, note };
    if (side === "owner" && amount) payload.amount = Number(amount);
    if (!confirm(side === "owner"
      ? `Liberar ${payload.amount ? formatBRL(payload.amount) : "o valor solicitado"} da caução para o DONO?`
      : "Devolver TODA a caução ao locatário?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/caucao-claims/${selected.id}/resolve`, payload);
      toast.success("Decisão registrada.");
      setSelected(null); setNote(""); setAmount("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao resolver");
    } finally { setBusy(false); }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="aj-card p-3 border-2 border-amber-300 bg-amber-50/40" data-testid="admin-caucao-claims-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="font-black text-amber-900 flex items-center gap-2">
          🛡 Débitos de caução contestados <span className="text-xs bg-amber-600 text-white rounded-full px-2 py-0.5" data-testid="admin-caucao-count">{items.length}</span>
        </div>
        <button onClick={load} className="text-[11px] font-bold text-amber-800 hover:underline" data-testid="admin-caucao-reload">Atualizar</button>
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="bg-white rounded-lg border-2 border-amber-200 p-3" data-testid={`admin-caucao-claim-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-800 text-sm truncate">{c.listing_title}</div>
                <div className="text-[11px] text-slate-500">
                  Valor: <b>{formatBRL(c.amount)}</b> · Caução: {formatBRL(c.caucao_total)} · Reserva #{c.booking_id.slice(-6)}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Solicitado em {new Date(c.created_at).toLocaleString("pt-BR")}
                  {c.contested_at && ` · Contestado em ${new Date(c.contested_at).toLocaleString("pt-BR")}`}
                </div>
              </div>
              <button onClick={() => { setSelected(c); setAmount(String(c.amount)); }}
                className="text-[11px] font-bold text-white bg-[#004E98] rounded-full px-3 py-1.5 shrink-0"
                data-testid={`admin-caucao-open-${c.id}`}>
                Analisar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de análise */}
      {selected && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={(e) => !busy && e.target === e.currentTarget && setSelected(null)} data-testid="admin-caucao-resolve-modal">
          <div className="bg-white w-full md:max-w-2xl md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-black text-lg text-[#004E98]">Analisar contestação de caução</h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="text-xs text-slate-600">
              Reserva #{selected.booking_id.slice(-6)} · Item: <b>{selected.listing_title}</b>
              <br />Caução retida: <b>{formatBRL(selected.caucao_total)}</b> · Solicitação do dono: <b>{formatBRL(selected.amount)}</b>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-lg border-2 border-red-200 bg-red-50/30 p-3">
                <div className="font-black text-red-900 text-sm mb-1">Solicitação do DONO</div>
                <div className="text-[11px] text-slate-700 whitespace-pre-wrap">{selected.reason}</div>
                <div className="grid grid-cols-3 gap-1.5 mt-2">
                  {(selected.evidence_files || []).map((p, i) => (
                    <a key={i} href={fileUrl(p)} target="_blank" rel="noopener noreferrer" className="aspect-square rounded overflow-hidden border border-red-300 block" data-testid={`admin-owner-evidence-${i}`}>
                      <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border-2 border-sky-200 bg-sky-50/30 p-3">
                <div className="font-black text-sky-900 text-sm mb-1">Defesa do LOCATÁRIO</div>
                <div className="text-[11px] text-slate-700 whitespace-pre-wrap">{selected.renter_defense || "(sem defesa)"}</div>
                <div className="grid grid-cols-3 gap-1.5 mt-2">
                  {(selected.renter_evidence || []).map((p, i) => (
                    <a key={i} href={fileUrl(p)} target="_blank" rel="noopener noreferrer" className="aspect-square rounded overflow-hidden border border-sky-300 block" data-testid={`admin-renter-evidence-${i}`}>
                      <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Nota do suporte (visível às partes)</span>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Ex.: risco confirmado nas fotos do dono, avaria consistente..."
                className="aj-input mt-1 resize-none" data-testid="admin-caucao-note" />
            </label>

            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Valor a liberar (se decidir pró-dono)</span>
              <input type="number" min="0" step="0.01" max={selected.caucao_total} value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="aj-input mt-1" data-testid="admin-caucao-amount" />
              <div className="text-[10px] text-slate-500 mt-0.5">Pode ajustar para menos que o solicitado. Máx: {formatBRL(selected.caucao_total)}</div>
            </label>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <button onClick={() => resolve("renter")} disabled={busy}
                className="btn-ghost border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 py-2 text-sm"
                data-testid="admin-caucao-resolve-renter">
                Liberar ao LOCATÁRIO
              </button>
              <button onClick={() => resolve("owner")} disabled={busy}
                className="btn-primary bg-amber-600 hover:bg-amber-700 disabled:opacity-50 py-2 text-sm"
                data-testid="admin-caucao-resolve-owner">
                Debitar em favor do DONO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function DisputesTab() {
  const [items, setItems] = useState([]);
  // V7.8: default = todas (antes era "open" e mostrava vazio quando só havia resolvidas)
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/admin/disputes", { params: { status: statusFilter } }).then((r) => setItems(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <div className="space-y-3" data-testid="admin-disputes">
      {/* V17.9 — Débitos de caução contestados (analisar) */}
      <AdminCaucaoClaimsPanel />

      <div className="flex gap-2">
        {[
          { key: "", label: "Todas" },
          { key: "open", label: "Abertas" },
          { key: "resolved", label: "Resolvidas" },
        ].map((s) => (
          <button
            key={s.key || "all"}
            onClick={() => setStatusFilter(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${statusFilter === s.key ? "border-[#004E98] bg-[#004E98] text-white" : "border-slate-200 bg-white text-slate-700"}`}
            data-testid={`dispute-filter-${s.key || "all"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="aj-card p-8 text-center text-slate-500 text-sm">Nenhuma disputa nesta categoria.</div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <button
              key={d.id}
              onClick={() => setSelected(d.id)}
              className="aj-card p-3 w-full text-left hover:bg-slate-50 transition"
              data-testid={`dispute-row-${d.id}`}
            >
              <div className="flex items-center gap-3">
                <Warning size={20} weight="fill" className={d.status === "open" ? "text-red-500" : "text-emerald-500"} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{d.booking?.listing_title || "—"}</div>
                  <div className="text-xs text-slate-500 truncate">
                    Aberta por {d.opened_by_name} · {formatDateTimeBR(d.created_at)}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">Motivo: {d.reason}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase font-bold tracking-wider text-slate-400">Repasse do locador</div>
                  <div className="font-heading font-black text-sm">{formatBRL(d.booking?.owner_net)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <DisputeModal disputeId={selected} onClose={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

const DISPUTE_STATUS = {
  open: { label: "Aberta", color: "bg-red-100 text-red-700", desc: "Disputa registrada — aguardando mediação." },
  admin_review: { label: "Em análise", color: "bg-amber-100 text-amber-700", desc: "Em mediação pela equipe AlugaJá." },
  bo_released: { label: "Dossiê de B.O. liberado", color: "bg-purple-100 text-purple-700", desc: "Dossiê liberado ao locador (não-devolução)." },
  resolved: { label: "Resolvida", color: "bg-emerald-100 text-emerald-700", desc: "Disputa marcada como resolvida." },
  closed: { label: "Encerrada", color: "bg-slate-200 text-slate-700", desc: "Mediação encerrada — sem movimentação de valores." },
  defaulted: { label: "Inadimplência", color: "bg-red-200 text-red-800", desc: "Registrada como calote." },
};

function DisputeModal({ disputeId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [msgTarget, setMsgTarget] = useState("both");

  const load = () => api.get(`/admin/disputes/${disputeId}`).then((r) => setData(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [disputeId]);

  const exportPdf = async (mode = "download") => {
    const native = isNative();
    // Preserva o gesto do usuário no web (evita bloqueio de popup) abrindo a aba antes do await.
    const w = (!native && mode === "view") ? window.open("", "_blank") : null;
    try {
      const r = await api.get(`/admin/disputes/${disputeId}/export-pdf`, { responseType: "blob", timeout: 120000 });
      const blob = new Blob([r.data], { type: "application/pdf" });
      if (native) {
        const res = await openPdfFile({ blob, fileName: `dossie_${disputeId}.pdf` });
        if (!res.ok && res.method === "none") toast.error("Não foi possível abrir o PDF.");
        return;
      }
      const url = URL.createObjectURL(blob);
      if (mode === "view") {
        if (w) w.location.href = url; else window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `dossie_${disputeId}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) try { w.close(); } catch { /* noop */ }
      toast.error("Erro ao gerar PDF");
    }
  };

  const approveNotReturned = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/disputes/${disputeId}/approve-not-returned`, { approve: true, note });
      toast.success("Não-devolução aprovada — dossiê liberado ao locador.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const sendMessage = async () => {
    if (!msg.trim()) return toast.warning("Escreva uma mensagem.");
    setBusy(true);
    try {
      await api.post(`/admin/disputes/${disputeId}/message`, { text: msg.trim(), to: msgTarget });
      toast.success("Mensagem enviada às partes.");
      setMsg("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const releaseDossie = async (to) => {
    if (!window.confirm(`Liberar o dossiê completo para o ${to === "renter" ? "locatário" : "locador"}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/disputes/${disputeId}/release-dossie`, { to, note });
      toast.success("Dossiê liberado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const closeDispute = async () => {
    if (!window.confirm("Encerrar esta disputa? Isso NÃO movimenta valores — apenas registra o fim da mediação.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/disputes/${disputeId}/close`, { note });
      toast.success("Disputa encerrada.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const { dispute, booking, listing, renter, owner } = data;
  const ended = ["resolved", "closed"].includes(dispute.status);
  const st = DISPUTE_STATUS[dispute.status] || { label: dispute.status, color: "bg-slate-100 text-slate-600", desc: "" };
  const released = dispute.dossie_released_to || [];

  if (viewer) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col" data-testid="dispute-doc-viewer">
        <div className="flex items-center justify-between p-3 bg-black/50 pt-safe">
          <button onClick={() => setViewer(null)} className="flex items-center gap-1.5 text-white font-bold text-sm" data-testid="doc-viewer-back">
            <ArrowLeft size={20} weight="bold" /> Voltar
          </button>
          <a href={viewer} download className="flex items-center gap-1 text-white/80 text-xs font-bold" data-testid="doc-viewer-download">
            <FileArrowDown size={16} weight="bold" /> Baixar
          </a>
        </div>
        <div className="flex-1 flex items-center justify-center overflow-auto p-4">
          <img src={viewer} alt="Documento da disputa" className="max-h-full max-w-full object-contain rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="dispute-modal">
      <div className="bg-white text-[#0F172A] w-full md:max-w-2xl md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10">
          <div>
            <div className="font-heading font-black text-lg">Disputa #{disputeId.slice(-6)}</div>
            <div className="text-xs text-slate-500">{listing?.title}</div>
          </div>
          <button onClick={onClose} data-testid="dispute-modal-close"><X size={22} weight="bold" /></button>
        </div>

        <div className="p-4 space-y-3 text-sm">
          <div className="aj-card p-3" data-testid="dispute-status-tracker">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Status da disputa</div>
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${st.color}`} data-testid="dispute-status-badge">{st.label}</span>
            </div>
            <p className="text-xs text-slate-600 mt-1">{st.desc}</p>
            {dispute.resolved_at && (
              <div className="text-[10px] text-slate-400 mt-1">Encerrada por {dispute.resolved_by_name || "—"} em {formatDateTimeBR(dispute.resolved_at)}</div>
            )}
            {released.length > 0 && (
              <div className="text-[10px] text-emerald-600 font-bold mt-1">Dossiê liberado para: {released.map((r) => r === "renter" ? "locatário" : "locador").join(", ")}</div>
            )}
          </div>

          <div className="aj-card p-3 bg-blue-50 border border-blue-200 text-xs" data-testid="dispute-parties-summary">
            <b>{renter?.name || "Locatário"}</b> (Locatário — quem alugou) alugou o item de <b>{owner?.name || "Locador"}</b> (Locador — dono do item).
            <div className="mt-1 text-slate-600">Disputa aberta por: <b>{dispute.opened_by_name || "—"}</b>{dispute.opened_by_user_id === (owner?.id) ? " (Locador)" : dispute.opened_by_user_id === (renter?.id) ? " (Locatário)" : " (Locador)"}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <PersonCard role="Locatário (quem alugou)" u={renter} />
            <PersonCard role="Locador (dono do item)" u={owner} />
          </div>

          <div className="aj-card p-3 space-y-1.5 text-xs">
            <Row label="Item" value={listing?.title} />
            <Row label="Período" value={`${fmtDateOnly(booking?.start_date)} → ${fmtDateOnly(booking?.end_date)}`} />
            <Row label="Valor cobrado" value={formatBRL(booking?.amount)} />
            <Row label="Repasse locador (líq.)" value={formatBRL(booking?.owner_net)} />
            <Row label="Status pagamento" value={booking?.payment_status} />
            <Row label="Status locação" value={booking?.status_locacao} />
            <Row label="Payout" value={booking?.payout_status} />
          </div>

          <div className="aj-card p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Motivo da Disputa</div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                {dispute.dispute_type === "not_returned" ? "Não devolvido" : "Dano"} · {dispute.status}
              </span>
            </div>
            <div className="text-sm font-semibold">{dispute.reason}</div>
            {dispute.description && <div className="text-xs text-slate-600 mt-1">{dispute.description}</div>}
            <div className="text-[10px] text-slate-400 mt-2">Aberta por {dispute.opened_by_name} em {formatDateTimeBR(dispute.created_at)}</div>
            {Array.isArray(dispute.photos) && dispute.photos.length > 0 && (
              <div className="flex gap-2 mt-2 overflow-x-auto" data-testid="dispute-evidence-gallery">
                {dispute.photos.map((p, i) => (
                  <button key={i} type="button" onClick={() => setViewer(`${API}/files/${p}`)} data-testid={`dispute-evidence-${i}`} className="shrink-0">
                    <img src={`${API}/files/${p}`} alt={`Prova ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border border-slate-200 hover:ring-2 hover:ring-[#004E98] transition" />
                  </button>
                ))}
              </div>
            )}
            {dispute.defense && (
              <div className="text-xs mt-2 p-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="dispute-defense-info">
                Defesa do locatário: <b>{dispute.defense.action === "accept" ? "Reconheceu o dano" : "Contestou"}</b>
                {dispute.defense.note ? ` — ${dispute.defense.note}` : ""}
              </div>
            )}
            {Array.isArray(dispute.quotes) && dispute.quotes.length > 0 && (
              <div className="text-xs mt-2 space-y-1" data-testid="dispute-quotes-info">
                <div className="font-bold text-slate-500">Orçamentos:</div>
                {dispute.quotes.map((q, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{i + 1}. {q.description || "Orçamento"}</span>
                    <b>{formatBRL(q.value)}{dispute.chosen_quote_index === i ? " ✓ escolhido" : ""}</b>
                  </div>
                ))}
              </div>
            )}
            {dispute.repair && (
              <div className="text-xs mt-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200">
                Conserto: <b>{formatBRL(dispute.repair.total_charged)}</b> · status {dispute.repair.status}
              </div>
            )}
          </div>

          {dispute.dispute_type === "not_returned" && !dispute.admin_approved_not_returned && !ended && (
            <div className="aj-card p-3 space-y-2 border-2 border-purple-200" data-testid="approve-not-returned-block">
              <div className="text-[10px] uppercase font-bold tracking-wider text-purple-600">Não-devolução — análise</div>
              <p className="text-xs text-slate-600">Aprovar libera ao locador o <b>Dossiê de B.O.</b> (dados do locatário + Art. 168).</p>
              <button onClick={approveNotReturned} disabled={busy} className="btn-primary w-full" data-testid="approve-not-returned-btn">
                {busy ? "Processando..." : "Aprovar não-devolução"}
              </button>
            </div>
          )}
          {dispute.admin_approved_not_returned && (
            <div className="aj-card p-3 text-xs text-purple-700 bg-purple-50 border border-purple-200">Não-devolução aprovada — dossiê liberado ao locador.</div>
          )}

          {/* Mediação — mensagem às partes */}
          <div className="aj-card p-3 space-y-2" data-testid="dispute-message-block">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Intermediar — mensagem às partes</div>
            <div className="grid grid-cols-3 gap-2">
              {[["both", "Ambos"], ["renter", "Locatário"], ["owner", "Locador"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMsgTarget(k)}
                  className={`p-2 rounded-lg border-2 text-xs font-bold ${msgTarget === k ? "border-[#004E98] bg-[#004E98]/5 text-[#004E98]" : "border-slate-200"}`}
                  data-testid={`msg-target-${k}`}>{l}</button>
              ))}
            </div>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Mensagem de mediação às partes..." className="aj-input" data-testid="dispute-message-input" />
            <button onClick={sendMessage} disabled={busy || !msg.trim()} className="btn-primary w-full disabled:opacity-50" data-testid="dispute-send-message-btn">
              <PaperPlaneTilt size={16} weight="bold" /> Enviar mensagem
            </button>
          </div>

          {/* Dossiê — visualizar / liberar */}
          <div className="aj-card p-3 space-y-2" data-testid="dispute-dossie-block">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Dossiê completo (B.O.)</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => exportPdf("view")} className="btn-ghost" data-testid="view-pdf-btn"><Eye size={16} weight="bold" /> Visualizar</button>
              <button onClick={() => exportPdf("download")} className="btn-ghost" data-testid="export-pdf-btn"><FileArrowDown size={16} weight="bold" /> Baixar PDF</button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => releaseDossie("renter")} disabled={busy}
                className={`p-2 rounded-lg border-2 text-xs font-bold ${released.includes("renter") ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200"}`}
                data-testid="release-dossie-renter">
                {released.includes("renter") ? `✓ Liberado ao Locatário${renter?.name ? ` (${renter.name})` : ""}` : `Liberar ao Locatário${renter?.name ? ` (${renter.name})` : ""}`}
              </button>
              <button onClick={() => releaseDossie("owner")} disabled={busy}
                className={`p-2 rounded-lg border-2 text-xs font-bold ${released.includes("owner") ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200"}`}
                data-testid="release-dossie-owner">
                {released.includes("owner") ? `✓ Liberado ao Locador${owner?.name ? ` (${owner.name})` : ""}` : `Liberar ao Locador${owner?.name ? ` (${owner.name})` : ""}`}
              </button>
            </div>
          </div>

          {/* Encerrar mediação (sem mover dinheiro) */}
          {!ended && (
            <div className="aj-card p-3 space-y-2" data-testid="dispute-close-block">
              <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Encerrar mediação</div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Observação interna / nota da decisão (opcional)" className="aj-input" data-testid="dispute-close-note" />
              <button onClick={closeDispute} disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="close-dispute-btn">
                <CheckCircle size={16} weight="bold" /> Encerrar disputa
              </button>
              <p className="text-[10px] text-slate-400">A liberação/devolução de valores segue o fluxo automático do escrow. A mediação apenas registra a decisão, libera dossiês e intermedia as partes.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── V10: Inadimplência (fila do calote) ─────────────────────────────────────
function DefaultersTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/defaulters").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const ban = async (u) => {
    if (!window.confirm(`Banir definitivamente ${u.name} (${u.doc_type === "cnpj" ? "CNPJ" : "CPF"} ${u.cpf})? Esta ação bloqueia o acesso à conta.`)) return;
    setBusy(u.id);
    try { await api.post(`/admin/users/${u.id}/ban`, { ban: true, reason: "Inadimplência reincidente" }); toast.success("Usuário banido."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const unban = async (u) => {
    setBusy(u.id);
    try { await api.post(`/admin/users/${u.id}/ban`, { ban: false }); toast.success("Banimento removido."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const lift = async (u) => {
    setBusy(u.id);
    try { await api.post(`/admin/users/${u.id}/lift-restriction`); toast.success("Restrição removida."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="admin-defaulters">
      <div className="aj-card p-3 text-xs text-slate-600 bg-amber-50 border border-amber-200">
        Lista de usuários restritos por inadimplência em disputas. Reincidência destacada. O banimento bloqueia o CPF/CNPJ no sistema.
      </div>
      {items == null ? (
        <div className="aj-card p-8 text-center text-slate-400">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="aj-card p-8 text-center text-slate-500" data-testid="defaulters-empty">Nenhum inadimplente. 🎉</div>
      ) : items.map((u) => (
        <div key={u.id} className="aj-card p-3 flex items-center justify-between gap-3" data-testid={`defaulter-row-${u.id}`}>
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">{u.name}
              {u.banned && <span className="ml-2 text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full">BANIDO</span>}
              {!u.banned && u.restricted && <span className="ml-2 text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full">RESTRITO</span>}
            </div>
            <div className="text-xs text-slate-500 truncate">{u.email} · {u.doc_type === "cnpj" ? "CNPJ" : "CPF"} {u.cpf || "—"}</div>
            {u.default_count > 0 && (
              <div className="text-[11px] text-red-600 font-bold mt-0.5">⚠️ Inadimplente em {u.disputes_defaulted || u.default_count} disputa(s)</div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            {!u.banned ? (
              <button onClick={() => ban(u)} disabled={busy === u.id} className="text-xs font-bold bg-red-600 text-white rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid={`ban-btn-${u.id}`}>Banir CPF/CNPJ</button>
            ) : (
              <button onClick={() => unban(u)} disabled={busy === u.id} className="text-xs font-bold bg-slate-600 text-white rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid={`unban-btn-${u.id}`}>Remover banimento</button>
            )}
            {u.restricted && !u.banned && (
              <button onClick={() => lift(u)} disabled={busy === u.id} className="text-xs font-bold border-2 border-slate-200 rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid={`lift-btn-${u.id}`}>Liberar restrição</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


function PersonCard({ role, u }) {
  if (!u) return <div className="aj-card p-3 text-xs text-slate-400">{role}: —</div>;
  return (
    <div className="aj-card p-3 text-xs space-y-0.5">
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{role}</div>
      <div className="font-bold text-sm truncate">{u.name}</div>
      <div className="font-mono text-[10px] text-slate-500">{u.support_id}</div>
      <div className="flex items-center gap-1 text-slate-600 truncate">
        <EnvelopeSimple size={11} weight="bold" />{u.email}
      </div>
      <div className="flex items-center gap-1 text-slate-600">
        <Phone size={11} weight="bold" />{u.phone || "—"}
      </div>
      <div className="flex items-center gap-1 text-slate-600">
        <IdentificationCard size={11} weight="bold" />
        {u.cpf ? u.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—"}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-right truncate">{value || "—"}</span>
    </div>
  );
}

// ============================================================
// V11.7: Contas excluídas (carência + anonimização)
// ============================================================
function DeletedAccountsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/deleted-accounts")
    .then((r) => setData(r.data)).catch(() => setData({ accounts: [], retention_years: 5 }));
  useEffect(() => { load(); }, []);

  const extend = async (a) => {
    const days = parseInt(window.prompt("Estender a carência por quantos dias?", "30") || "0", 10);
    if (!days || days < 1) return;
    setBusy(a.id);
    try { await api.post(`/admin/deleted-accounts/${a.id}/extend`, { days }); toast.success(`Prazo estendido em ${days} dias.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const restore = async (a) => {
    if (!window.confirm(`Restaurar a conta de ${a.name || a.email}? O login e os anúncios serão reativados.`)) return;
    setBusy(a.id);
    try { await api.post(`/admin/deleted-accounts/${a.id}/restore`); toast.success("Conta restaurada."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const accounts = data?.accounts || [];
  const [filter, setFilter] = useState("all"); // all | user | admin
  const filtered = accounts.filter((a) => filter === "all" || a.deleted_by_type === filter);
  return (
    <div className="space-y-3" data-testid="admin-deleted-accounts">
      <div className="aj-card p-3 text-xs text-slate-600 bg-slate-50 border border-slate-200">
        Histórico de contas excluídas — pelo <b>próprio usuário</b> (LGPD, carência de 30 dias / anonimizadas)
        ou pelo <b>admin</b> (exclusão manual). Registros financeiros são retidos anonimizados por
        <b> {data?.retention_years ?? 5} anos</b>. Com disputa aberta, a anonimização fica <b>suspensa</b> até a resolução.
      </div>
      <div className="flex gap-2" data-testid="deleted-filter">
        {[{ k: "all", l: "Todas" }, { k: "user", l: "Pelo usuário" }, { k: "admin", l: "Pelo admin" }].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${filter === f.k ? "bg-[#004E98] text-white border-[#004E98]" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`deleted-filter-${f.k}`}
          >
            {f.l} {f.k !== "all" && `(${accounts.filter((a) => a.deleted_by_type === f.k).length})`}
          </button>
        ))}
      </div>
      {data == null ? (
        <div className="aj-card p-8 text-center text-slate-400">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="aj-card p-8 text-center text-slate-500" data-testid="deleted-empty">Nenhuma conta neste filtro. 🎉</div>
      ) : filtered.map((a) => (
        <div key={a.id} className="aj-card p-3 flex items-center justify-between gap-3" data-testid={`deleted-row-${a.id}`}>
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">
              {a.name || "—"}
              {a.deleted_by_type === "admin"
                ? <span className="ml-2 text-[10px] bg-red-700 text-white px-2 py-0.5 rounded-full" data-testid={`deleted-badge-admin-${a.id}`}>EXCLUÍDA PELO ADMIN</span>
                : a.deletion_status === "purged"
                  ? <span className="ml-2 text-[10px] bg-slate-700 text-white px-2 py-0.5 rounded-full">ANONIMIZADA</span>
                  : <span className="ml-2 text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full">EM CARÊNCIA</span>}
              {a.hold_dispute && <span className="ml-2 text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full">DISPUTA</span>}
            </div>
            <div className="text-xs text-slate-500 truncate">{a.email} · ID {a.support_id || "—"}</div>
            {a.deleted_by_type === "admin" ? (
              <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`deleted-by-${a.id}`}>
                Excluída por <b>{a.deleted_by_name || "Admin"}</b>{a.deleted_at ? ` em ${(a.deleted_at || "").slice(0, 10)}` : ""}
              </div>
            ) : a.deletion_status === "pending_deletion" ? (
              <div className="text-[11px] text-slate-600 mt-0.5" data-testid={`deleted-countdown-${a.id}`}>
                ⏳ Faltam <b>{a.days_remaining ?? "?"}</b> dia(s) para a anonimização automática
                {a.hold_dispute && <span className="text-red-600 font-bold"> (suspenso por disputa)</span>}
              </div>
            ) : (
              <div className="text-[11px] text-slate-400 mt-0.5">Anonimizada em {(a.deleted_at || "").slice(0, 10)}</div>
            )}
          </div>
          {a.can_restore && (
            <div className="flex flex-col gap-1.5 shrink-0">
              <button onClick={() => extend(a)} disabled={busy === a.id} className="text-xs font-bold border-2 border-slate-200 rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid={`extend-btn-${a.id}`}>Estender prazo</button>
              <button onClick={() => restore(a)} disabled={busy === a.id} className="text-xs font-bold bg-[#004E98] text-white rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid={`restore-btn-${a.id}`}>Restaurar</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


// ============================================================
// Listings moderation
// ============================================================

function ListingsModerationTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [removing, setRemoving] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get("/admin/listings", { params: { status: statusFilter } }).then((r) => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const moderate = async (id, action, motivo = "") => {
    try {
      await api.post(`/admin/listings/${id}/moderate`, { action, reason: motivo });
      toast.success(`Ação aplicada: ${action}`);
      load();
      setRemoving(null); setReason("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-listings">
      <div className="flex gap-2">
        {[
          { key: "active", label: "Ativos" },
          { key: "paused", label: "Pausados" },
          { key: "removed", label: "Removidos" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${statusFilter === s.key ? "border-[#004E98] bg-[#004E98] text-white" : "border-slate-200 bg-white text-slate-700"}`}
            data-testid={`listings-filter-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="aj-card p-8 text-center text-slate-500 text-sm">Nenhum anúncio nesta categoria.</div>
      ) : (
        <div className="space-y-2">
          {items.map((l) => (
            <div key={l.id} className="aj-card p-3 flex items-center gap-3" data-testid={`listing-row-${l.id}`}>
              <div className="w-12 h-12 rounded bg-slate-200 flex-shrink-0 overflow-hidden">
                {l.images?.[0] && <img src={`${API}/files/${l.images[0]}`} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{l.title}</div>
                <div className="text-xs text-slate-500 truncate">{l.owner_name} · {l.city}/{l.uf} · {formatBRL(l.price_per_day)}/{unitLabelForItem(l)[0]}</div>
                {l.removal_reason && <div className="text-xs text-red-600 mt-0.5">Removido: {l.removal_reason}</div>}
              </div>
              <div className="flex items-center gap-1">
                {statusFilter !== "active" && (
                  <button
                    onClick={() => moderate(l.id, "approve")}
                    className="px-2 py-1.5 rounded-full inline-flex items-center gap-1 hover:bg-emerald-50 text-emerald-600 text-xs font-bold"
                    title="Aprovar"
                    data-testid={`moderation-approve-btn-${l.id}`}
                  >
                    <CheckCircle size={16} weight="bold" />
                    <span className="hidden md:inline">Aprovar</span>
                  </button>
                )}
                {statusFilter === "active" && (
                  <button
                    onClick={() => moderate(l.id, "pause")}
                    className="px-2 py-1.5 rounded-full inline-flex items-center gap-1 hover:bg-amber-50 text-amber-600 text-xs font-bold"
                    title="Pausar"
                    data-testid={`moderation-pause-btn-${l.id}`}
                  >
                    <Pause size={16} weight="bold" />
                    <span className="hidden md:inline">Pausar</span>
                  </button>
                )}
                {statusFilter !== "removed" && (
                  <button
                    onClick={() => setRemoving(l)}
                    className="px-2 py-1.5 rounded-full inline-flex items-center gap-1 hover:bg-red-50 text-red-600 text-xs font-bold"
                    title="Remover"
                    data-testid={`moderation-remove-btn-${l.id}`}
                  >
                    <Trash size={16} weight="bold" />
                    <span className="hidden md:inline">Remover</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {removing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setRemoving(null)}>
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="font-heading font-black text-lg mb-1">Remover anúncio</div>
            <p className="text-sm text-slate-500 mb-3">Esta ação envia um e-mail ao proprietário e oculta o anúncio. Informe o motivo:</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="aj-input"
              placeholder="Ex.: Foto inadequada, item proibido, preço abusivo..."
              data-testid="remove-reason-input"
            />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={() => { setRemoving(null); setReason(""); }} className="btn-ghost">Cancelar</button>
              <button
                onClick={() => moderate(removing.id, "remove", reason.trim())}
                disabled={!reason.trim()}
                className="btn-primary disabled:opacity-50"
                data-testid="confirm-remove-btn"
              >
                Confirmar remoção
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Users tab
function DetailItem({ label, value }) {
  return (
    <div className="bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">
      <div className="text-[9px] uppercase tracking-wide text-slate-400 font-bold">{label}</div>
      <div className="text-slate-800 font-semibold truncate">{value}</div>
    </div>
  );
}
// ============================================================

function AsaasChargeTool() {
  const [pid, setPid] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stuck, setStuck] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const loadStuck = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get(`/admin/asaas/stuck`);
      setStuck(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoadingList(false); }
  };

  useEffect(() => { loadStuck(); }, []);

  const scanNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/asaas/stuck/scan`);
      toast.success(`Varredura concluída: ${data.released} liberada(s), ${data.still_stuck} ainda travada(s).`);
      loadStuck();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const releaseBooking = async (it) => {
    if (!window.confirm(`Liberar/estornar a cobrança de "${it.listing_title || "reserva"}" (${it.user_name || it.user_email || ""})?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/asaas/stuck/${it.booking_id}/release`);
      toast.success(data.ok ? `Liberada. Status: ${data.status_after || "ok"}` : "Não foi possível liberar — veja o painel Asaas.");
      loadStuck();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const consultar = async () => {
    const id = pid.trim();
    if (!id) return;
    setBusy(true); setInfo(null);
    try {
      const { data } = await api.get(`/admin/asaas/payment/${id}`);
      setInfo(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const liberar = async () => {
    const id = pid.trim();
    if (!id) return;
    if (!window.confirm(`Liberar/estornar a cobrança ${id}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/asaas/payment/${id}/release`);
      toast.success(`Processado. Status: ${data.status_before || "?"} → ${data.status_after || "removida"}`);
      consultar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="aj-card p-4 space-y-3" data-testid="asaas-charge-tool">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Cobranças de cartão travadas (pré-autorização pendurada)</div>
        <button onClick={scanNow} disabled={busy} className="text-xs font-bold text-[#004E98] inline-flex items-center gap-1 disabled:opacity-50" data-testid="asaas-scan-btn">
          <ArrowCounterClockwise size={13} weight="bold" /> Varredura agora
        </button>
      </div>

      {loadingList ? (
        <div className="text-xs text-slate-400">Carregando...</div>
      ) : stuck.length === 0 ? (
        <div className="text-xs text-emerald-600 font-semibold" data-testid="asaas-no-stuck">✓ Nenhuma cobrança travada. O sistema libera automaticamente.</div>
      ) : (
        <div className="space-y-2" data-testid="asaas-stuck-list">
          {stuck.map((it) => (
            <div key={it.booking_id} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs" data-testid={`stuck-${it.booking_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{it.user_name || it.user_email || "Usuário"}</div>
                <div className="text-slate-600 truncate">{it.listing_title || "—"} · {it.status} · {formatBRL(it.amount)}</div>
              </div>
              <button onClick={() => releaseBooking(it)} disabled={busy} className="px-2.5 py-1.5 rounded-lg bg-amber-500 text-white font-bold shrink-0 disabled:opacity-50" data-testid={`release-stuck-${it.booking_id}`}>Liberar</button>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setShowManual((v) => !v)} className="text-[11px] text-slate-400 underline" data-testid="asaas-manual-toggle">
        {showManual ? "Ocultar" : "Liberar por ID da cobrança (avançado)"}
      </button>
      {showManual && (
        <div className="space-y-2 pt-1">
          <div className="flex gap-2 items-center">
            <input
              value={pid}
              onChange={(e) => setPid(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && consultar()}
              placeholder="ID da cobrança Asaas (ex.: pay_xxx)"
              className="aj-input flex-1"
              data-testid="asaas-payment-id-input"
            />
            <button onClick={consultar} disabled={busy} className="btn-blue px-3" data-testid="asaas-consult-btn">Consultar</button>
            <button onClick={liberar} disabled={busy} className="px-3 py-2 rounded-xl bg-amber-500 text-white font-bold text-sm disabled:opacity-50" data-testid="asaas-release-btn">Liberar/Estornar</button>
          </div>
          {info && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1" data-testid="asaas-payment-info">
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-bold">{info.status}{info.deleted ? " (removida)" : ""}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tipo / Valor</span><span className="font-semibold">{info.billingType} · {formatBRL(info.value)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-mono">{info.customer}</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function UsersTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [toDelete, setToDelete] = useState(null);
  const [deleteCascade, setDeleteCascade] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});
  const [cityFilter, setCityFilter] = useState("");

  const toggleExpand = async (u) => {
    if (expandedId === u.id) { setExpandedId(null); return; }
    setExpandedId(u.id);
    if (!details[u.id]) {
      try {
        const { data } = await api.get(`/admin/users/${u.id}`);
        setDetails((d) => ({ ...d, [u.id]: { ...data.summary, wallet_history: data.wallet_history || [] } }));
      } catch { /* noop */ }
    }
  };

  const load = () => {
    const params = { limit: 100 };
    if (q.trim()) params.q = q.trim();
    if (cityFilter.trim()) params.city = cityFilter.trim();
    if (sort === "last_seen") params.sort = "last_seen";
    api.get("/admin/users", { params }).then((r) => setItems(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [sort]);

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "user" : "admin";
    setBusy(true);
    try {
      await api.post("/admin/promote", { user_id: u.id, role: newRole });
      toast.success(newRole === "admin" ? `${u.name} agora é admin` : `${u.name} rebaixado(a) a usuário`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${toDelete.id}`, { params: { cascade: deleteCascade } });
      toast.success(`Usuário excluído. ${data.listings_affected} anúncio(s) removido(s).`);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resetStrikes = async (u) => {
    if (!window.confirm(`Resetar os strikes de cancelamento por falta de estoque de ${u.name}? Isso zera o contador e a multa pendente (${formatBRL(u.pending_debt || 0)}).`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}/reset-strikes`);
      toast.success("Strikes resetados.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isDeleted = (u) => !!u.deleted_at;
  const lastSeenLabel = (u) => {
    if (!u.last_seen_at) return "Nunca";
    try {
      const d = new Date(u.last_seen_at);
      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (days === 0) return "Hoje";
      if (days === 1) return "Ontem";
      return `${days}d atrás`;
    } catch { return "—"; }
  };

  return (
    <div className="space-y-3" data-testid="admin-users">
      <div className="aj-card p-4 space-y-3">
        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Atribuir/Remover Admin</div>
        <PromoteByEmail onDone={load} />
      </div>

      <AsaasChargeTool />

      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar nome, email, CPF, ID..."
            className="aj-input pl-9"
            data-testid="users-search-input"
          />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="aj-input w-auto" data-testid="users-sort-select">
          <option value="recent">Mais recentes</option>
          <option value="last_seen">Último acesso</option>
          <option value="oldest">Mais antigos</option>
        </select>
        <button onClick={load} className="btn-blue px-3" data-testid="users-search-btn"><MagnifyingGlass size={14} weight="bold" /></button>
      </div>

      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <MapPin size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Filtrar por cidade (ex: Sinop)..."
            className="aj-input pl-9"
            data-testid="users-city-filter-input"
          />
        </div>
        {cityFilter.trim() && (
          <button onClick={() => { setCityFilter(""); setTimeout(load, 0); }} className="text-xs font-bold text-slate-500 px-2" data-testid="users-city-clear-btn">Limpar</button>
        )}
        <button onClick={load} className="btn-blue px-3" data-testid="users-city-filter-btn"><MagnifyingGlass size={14} weight="bold" /></button>
      </div>

      <div className="text-xs text-slate-500 font-bold px-1" data-testid="users-count">
        {items.length} usuário(s){cityFilter.trim() ? ` em "${cityFilter.trim()}"` : ""}
      </div>

      <div className="space-y-2">
        {items.map((u) => (
          <div key={u.id} data-testid={`user-block-${u.id}`}>
          <div className={`aj-card p-3 flex items-center gap-3 ${isDeleted(u) ? "opacity-60 bg-slate-50" : ""}`} data-testid={`user-row-${u.id}`}>
            <button onClick={() => toggleExpand(u)} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#004E98] to-[#FF6B35] text-white flex items-center justify-center font-bold text-sm shrink-0" data-testid={`user-expand-${u.id}`}>
              {u.name?.[0]?.toUpperCase() || "?"}
            </button>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleExpand(u)}>
              <div className="text-sm font-bold truncate flex items-center gap-1">
                {u.name}
                {" "}<span className="text-[10px] font-mono text-slate-400">{u.support_id}</span>
                <CaretDown size={12} weight="bold" className={`text-slate-400 transition-transform ${expandedId === u.id ? "rotate-180" : ""}`} />
              </div>
              <div className="text-xs text-slate-500 truncate">{u.email}</div>
              <div className="text-[10px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                <Clock size={9} weight="bold" /> {lastSeenLabel(u)}
                {u.city && <span className="inline-flex items-center gap-0.5 text-slate-500 font-bold"><MapPin size={9} weight="bold" /> {u.city}{u.uf ? `/${u.uf}` : ""}</span>}
                {isDeleted(u) && <span className="aj-badge bg-red-100 text-red-700 ml-1 px-1.5 py-0 rounded-full text-[9px] font-bold">Excluído</span>}
              </div>
              {(u.owner_cancel_count > 0 || u.pending_debt > 0) && (
                <div className="text-[11px] mt-1 flex flex-wrap items-center gap-1.5" data-testid={`user-strikes-${u.id}`}>
                  {u.owner_cancel_count > 0 && (
                    <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">⚠️ {u.owner_cancel_count} cancelamento(s) por falta de estoque</span>
                  )}
                  {u.pending_debt > 0 && (
                    <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">Multa pendente: {formatBRL(u.pending_debt)}</span>
                  )}
                </div>
              )}
            </div>
            {u.role === "admin" && <span className="aj-badge aj-badge-orange">Admin</span>}
            <div className="flex items-center gap-1">
              {!isDeleted(u) && (
                <button
                  onClick={() => toggleRole(u)}
                  disabled={busy}
                  className={`p-2 rounded-full ${u.role === "admin" ? "hover:bg-red-50 text-red-600" : "hover:bg-emerald-50 text-emerald-600"}`}
                  title={u.role === "admin" ? "Rebaixar" : "Promover a admin"}
                  data-testid={`toggle-role-${u.id}`}
                >
                  {u.role === "admin" ? <MinusCircle size={18} weight="bold" /> : <PlusCircle size={18} weight="bold" />}
                </button>
              )}
              {!isDeleted(u) && (u.owner_cancel_count > 0 || u.pending_debt > 0) && (
                <button
                  onClick={() => resetStrikes(u)}
                  disabled={busy}
                  className="p-2 rounded-full hover:bg-amber-50 text-amber-600"
                  title="Resetar strikes / multa por falta de estoque"
                  data-testid={`reset-strikes-${u.id}`}
                >
                  <ArrowCounterClockwise size={18} weight="bold" />
                </button>
              )}
              {!isDeleted(u) && (
                <button
                  onClick={() => { setToDelete(u); setDeleteCascade(true); }}
                  disabled={busy}
                  className="p-2 rounded-full hover:bg-red-50 text-red-600"
                  title="Excluir usuário"
                  data-testid={`delete-user-${u.id}`}
                >
                  <Trash size={18} weight="bold" />
                </button>
              )}
            </div>
          </div>
          {expandedId === u.id && (
            <div className="aj-card mt-1 p-3 bg-slate-50/80 border-t-2 border-[#004E98]/10" data-testid={`user-details-${u.id}`}>
              {!details[u.id] ? (
                <div className="text-xs text-slate-400">Carregando...</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <DetailItem label="Cidade" value={details[u.id].city ? `${details[u.id].city}${details[u.id].uf ? " - " + details[u.id].uf : ""}` : "—"} />
                  <DetailItem label="Telefone" value={details[u.id].phone || "—"} />
                  <DetailItem label="Anúncios ativos" value={`${details[u.id].active_listings} / ${details[u.id].total_listings}`} />
                  <DetailItem label="Indicados (pagos)" value={details[u.id].referrals_rewarded} />
                  <DetailItem label="Indicados (pendentes)" value={details[u.id].referrals_pending} />
                  <DetailItem label="Locações como dono" value={details[u.id].completed_as_owner} />
                  <DetailItem label="Locações como cliente" value={details[u.id].completed_as_renter} />
                  <DetailItem label="Saldo carteira" value={formatBRL(details[u.id].wallet_balance || 0)} />
                  <DetailItem label="Código de indicação" value={details[u.id].referral_code || "—"} />
                  {details[u.id].invited_by_code && <DetailItem label="Indicado por" value={`${details[u.id].invited_by_name || "—"} (${details[u.id].invited_by_code})`} />}
                  <DetailItem label="KYC" value={details[u.id].status === "ativo_verificado" ? "Verificado" : (details[u.id].kyc_status || "—")} />
                  <DetailItem label="Tipo" value={(details[u.id].doc_type || "").toUpperCase() === "CNPJ" ? "PJ" : "PF"} />
                </div>
              )}
              {details[u.id] && (
                <div className="mt-3" data-testid={`user-wallet-history-${u.id}`}>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center gap-1 mb-1.5">
                    <Wallet size={12} weight="bold" /> Movimentação financeira (carteira)
                  </div>
                  {(details[u.id].wallet_history || []).length === 0 ? (
                    <div className="text-xs text-slate-400" data-testid={`wallet-history-empty-${u.id}`}>Sem movimentações registradas.</div>
                  ) : (
                    <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                      {(details[u.id].wallet_history || []).map((w, i) => {
                        const isCredit = w.direction === "credit";
                        return (
                          <div key={w.id || i} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid={`wallet-history-item-${u.id}-${i}`}>
                            <div className="min-w-0">
                              <div className="text-xs font-bold text-slate-700 truncate">{w.description || w.source || "Movimentação"}</div>
                              <div className="text-[10px] text-slate-400">{(w.created_at || "").slice(0, 16).replace("T", " ")}{w.source ? ` · ${w.source}` : ""}</div>
                            </div>
                            <div className={`text-xs font-black shrink-0 ${isCredit ? "text-emerald-600" : "text-red-600"}`}>
                              {isCredit ? "+" : "-"}{formatBRL(Math.abs(Number(w.amount) || 0))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
        ))}
      </div>

      {toDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setToDelete(null)} data-testid="delete-user-modal">
          <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Trash size={22} weight="fill" className="text-red-600" />
              <h3 className="font-heading font-black text-lg">Excluir usuário</h3>
            </div>
            <p className="text-sm text-slate-700">
              Tem certeza que deseja excluir <b>{toDelete.name}</b> (<span className="font-mono text-xs">{toDelete.support_id}</span>)?
              Esta ação anonimiza os dados pessoais (e-mail, CPF, telefone) e mantém o histórico para auditoria.
            </p>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={deleteCascade}
                onChange={(e) => setDeleteCascade(e.target.checked)}
                className="w-4 h-4 accent-[#FF6B35]"
                data-testid="delete-cascade-checkbox"
              />
              Remover também os anúncios deste usuário
            </label>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              ⚠️ Não é possível excluir usuários com locações ativas (em andamento, aceite ou pagamento pendente).
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setToDelete(null)} className="btn-ghost" data-testid="cancel-delete-user">Cancelar</button>
              <button onClick={confirmDelete} disabled={busy} className="px-3 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50" data-testid="confirm-delete-user">
                {busy ? "Excluindo..." : "Confirmar exclusão"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Analytics Tab — growth & user insights
// ============================================================

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [hideMoney, setHideMoney] = useState(() => localStorage.getItem("adm_hide_money") === "1");

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data));
  }, []);

  const toggleMoney = () => {
    setHideMoney((v) => { localStorage.setItem("adm_hide_money", v ? "0" : "1"); return !v; });
  };

  if (!data) return <div className="text-center py-8 text-slate-500">Carregando insights...</div>;

  const { totals, signup_series_30d, top_owners_by_listings, top_owners_by_revenue, top_renters, users_by_city, visitors_by_city, rentals_by_period, top_listings_rented } = data;
  const brl = (n) => hideMoney ? "R$ ••••" : (n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const rp = rentals_by_period || {};
  const periodCards = [
    { key: "today", label: "Hoje" },
    { key: "week", label: "Esta semana" },
    { key: "month", label: "Este mês" },
    { key: "total", label: "Total" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-analytics">
      {/* Aluguéis por período — quantidade + faturamento (R$) */}
      <div className="aj-card p-4" data-testid="rentals-by-period">
        <div className="font-heading font-bold text-sm mb-3 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Receipt size={16} weight="duotone" className="text-[#004E98]" /> Aluguéis (pagos) por período</span>
          <button
            onClick={toggleMoney}
            className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-[#004E98] border border-slate-200 rounded-full px-2.5 py-1"
            data-testid="toggle-money-btn"
            title={hideMoney ? "Mostrar valores" : "Ocultar valores"}
          >
            {hideMoney ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
            {hideMoney ? "Mostrar R$" : "Ocultar R$"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {periodCards.map((p) => (
            <div key={p.key} className="rounded-xl border-2 border-slate-100 bg-slate-50/60 p-3" data-testid={`rentals-period-${p.key}`}>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{p.label}</div>
              <div className="text-2xl font-black text-[#004E98] leading-tight mt-1" data-testid={`rentals-count-${p.key}`}>
                {rp[p.key]?.count || 0}
              </div>
              <div className="text-[11px] text-slate-500 font-semibold">aluguéis</div>
              <div className="text-sm font-black text-emerald-600 mt-1.5" data-testid={`rentals-revenue-${p.key}`}>
                {brl(rp[p.key]?.revenue)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ranking dos itens mais locados */}
      <div className="aj-card p-4" data-testid="top-listings-rented-card">
        <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
          <Crown size={16} weight="duotone" className="text-amber-500" /> Itens mais locados
        </div>
        {(top_listings_rented || []).length === 0 ? (
          <div className="text-xs text-slate-400">Nenhum aluguel pago ainda.</div>
        ) : (
          <div className="space-y-2" data-testid="top-listings-rented-list">
            {(top_listings_rented || []).map((it, i) => (
              <div key={it.listing_id} className="flex items-center justify-between gap-2 text-sm border-b border-slate-50 pb-2 last:border-0" data-testid={`top-listing-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-[#004E98]/10 text-[#004E98] font-black text-xs flex items-center justify-center">{i + 1}</span>
                  <span className="text-slate-800 font-semibold truncate">{it.title}</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-black text-emerald-600">{brl(it.revenue)}</div>
                  <div className="text-[11px] text-slate-500 font-semibold">{it.rentals} locaç{it.rentals === 1 ? "ão" : "ões"} · {it.days} dia{it.days === 1 ? "" : "s"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Total de Usuários" value={totals.total_users} icon={UserCircle} color="#004E98" testId="metric-total-users" />
        <Metric label="Novos (30 dias)" value={totals.new_users_30d} icon={TrendUp} color="#16A34A" testId="metric-new-users" />
        <Metric label="Ativos (30 dias)" value={totals.active_30d} icon={CheckCircle} color="#10B981" testId="metric-active-users" />
        <Metric label="Inativos" value={totals.inactive_30d} icon={UserMinus} color="#94A3B8" testId="metric-inactive-users" />
        <Metric label="Identidade Verificada" value={totals.kyc_verified} icon={Shield} color="#0EA5E9" testId="metric-kyc-verified" />
        <Metric label="Verificações Pendentes" value={totals.kyc_pending} icon={IdentificationCard} color="#FF6B35" testId="metric-kyc-pending" />
        <Metric label="Excluídos" value={totals.deleted_users} icon={Trash} color="#DC2626" testId="metric-deleted-users" />
      </div>

      {/* V14.6: visitantes (anônimos + logados) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Metric label="Visitantes (total)" value={totals.visitors_total || 0} icon={Users} color="#7C3AED" testId="metric-visitors-total" />
        <Metric label="Visitantes anônimos" value={totals.visitors_anon || 0} icon={UserCircle} color="#A855F7" testId="metric-visitors-anon" />
        <Metric label="Visitantes (30 dias)" value={totals.visitors_30d || 0} icon={TrendUp} color="#9333EA" testId="metric-visitors-30d" />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="aj-card p-4" data-testid="users-by-city-card">
          <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
            <MapPin size={16} weight="bold" className="text-[#004E98]" /> Usuários cadastrados por cidade
          </div>
          {(users_by_city || []).length === 0 ? (
            <div className="text-xs text-slate-400">Sem dados de cidade ainda.</div>
          ) : (
            <div className="space-y-1.5" data-testid="users-by-city-list">
              {(users_by_city || []).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm" data-testid={`user-city-${i}`}>
                  <span className="text-slate-700 truncate">{c.city}{c.uf ? ` - ${c.uf}` : ""}</span>
                  <span className="font-bold text-[#004E98]">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="aj-card p-4" data-testid="visitors-by-city-card">
          <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
            <MapPin size={16} weight="bold" className="text-[#7C3AED]" /> Visitantes por cidade
          </div>
          {(visitors_by_city || []).length === 0 ? (
            <div className="text-xs text-slate-400">Coletando visitas (a partir de agora).</div>
          ) : (
            <div className="space-y-1.5" data-testid="visitors-by-city-list">
              {(visitors_by_city || []).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm" data-testid={`visitor-city-${i}`}>
                  <span className="text-slate-700 truncate">{c.city}{c.uf ? ` - ${c.uf}` : ""}</span>
                  <span className="font-bold text-[#7C3AED]">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="aj-card p-4">
        <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
          <TrendUp size={16} weight="bold" /> Cadastros — últimos 30 dias
        </div>
        <BarChartSimple series={signup_series_30d || []} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="aj-card p-4">
          <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
            <Crown size={16} weight="duotone" className="text-amber-500" /> Top Locadores (anúncios)
          </div>
          <TopList items={top_owners_by_listings} valueLabel={(x) => `${x.count} itens`} testId="top-by-listings" />
        </div>
        <div className="aj-card p-4">
          <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
            <Coins size={16} weight="duotone" className="text-emerald-600" /> Top Locadores (receita)
          </div>
          <TopList
            items={top_owners_by_revenue}
            valueLabel={(x) => `${brl(x.revenue)} · ${x.rentals} loc.`}
            testId="top-by-revenue"
          />
        </div>
      </div>

      <div className="aj-card p-4">
        <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">
          <Receipt size={16} weight="duotone" className="text-[#FF6B35]" /> Top Locatários (clientes)
        </div>
        <TopList
          items={top_renters}
          valueLabel={(x) => `${x.count} loc. · ${brl(x.spend)}`}
          testId="top-renters"
        />
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon, color, testId }) {
  return (
    <div className="aj-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase font-bold tracking-wider">
        <Icon size={14} weight="bold" color={color} />
        <span className="truncate">{label}</span>
      </div>
      <div className="font-heading font-black text-2xl mt-2" style={{ color }}>{value}</div>
    </div>
  );
}

function BarChartSimple({ series }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div className="flex items-end gap-1 h-24" data-testid="analytics-chart">
      {series.map((s) => {
        const h = (s.count / max) * 100;
        return (
          <div key={s.date} className="flex-1 flex flex-col items-center justify-end" title={`${s.date}: ${s.count} cadastro(s)`}>
            <div
              className="w-full bg-gradient-to-t from-[#004E98] to-[#0EA5E9] rounded-t"
              style={{ height: `${h}%`, minHeight: s.count > 0 ? 2 : 0 }}
            />
          </div>
        );
      })}
    </div>
  );
}

function TopList({ items, valueLabel, testId }) {
  if (!items || items.length === 0) {
    return <div className="text-xs text-slate-400 py-4 text-center">Sem dados ainda.</div>;
  }
  return (
    <ol className="space-y-1.5" data-testid={testId}>
      {items.map((x, i) => (
        <li key={x.user_id || i} className="flex items-center gap-2 text-sm">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${i < 3 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
            {i + 1}
          </span>
          <span className="flex-1 truncate font-semibold">{x.name}</span>
          <span className="text-xs text-slate-600 font-mono">{valueLabel(x)}</span>
        </li>
      ))}
    </ol>
  );
}

function PromoteByEmail({ onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/promote", { email: email.trim().toLowerCase(), role: "admin" });
      toast.success(`${email} promovido(a) a admin`);
      setEmail("");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-2">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email@exemplo.com"
        className="aj-input"
        data-testid="promote-email-input"
      />
      <button onClick={submit} disabled={busy || !email.trim()} className="btn-primary whitespace-nowrap" data-testid="promote-submit-btn">
        <PlusCircle size={16} weight="bold" /> Promover
      </button>
    </div>
  );
}

// ============================================================
// KYC Tab — Document validation queue
// ============================================================

// V7.8: preview robusto — detecta PDF e mostra link, trata erro de imagem
// V15.x: abre um arquivo protegido (PDF/doc KYC) com autenticação (Bearer) via blob.
// Corrige "acesso negado" no celular/app, onde o link direto não envia o header de auth.
// No app nativo o WebView não renderiza blob:// em nova aba → usamos openPdfFile (Share nativo).
async function openAuthedFile(path) {
  const native = isNative();
  const w = native ? null : window.open("", "_blank");
  try {
    const r = await api.get(`/files/${path}`, { responseType: "blob", timeout: 120000 });
    const isPdf = /\.pdf(\?|$)/i.test(path);
    const fileName = (path.split("/").pop() || "documento").split("?")[0];
    if (native) {
      const blob = isPdf ? new Blob([r.data], { type: "application/pdf" }) : r.data;
      const res = await openPdfFile({ blob, fileName });
      if (!res.ok && res.method === "none") toast.error("Não foi possível abrir o documento.");
      return;
    }
    const blob = isPdf ? new Blob([r.data], { type: "application/pdf" }) : r.data;
    const url = URL.createObjectURL(blob);
    if (w) w.location.href = url; else window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (w) try { w.close(); } catch { /* noop */ }
    toast.error("Não foi possível abrir o documento.");
  }
}

function KycDocPreview({ path, label }) {
  const [errored, setErrored] = useState(false);
  if (!path) return null;
  const url = `${API}/files/${path}`;
  const isPdf = /\.pdf(\?|$)/i.test(path);
  return (
    <div>
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 mb-1">{label}</div>
      {isPdf ? (
        <button
          type="button"
          onClick={() => openAuthedFile(path)}
          className="w-full flex items-center gap-2 px-3 py-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-sm font-bold text-[#004E98]"
          data-testid={`kyc-doc-pdf-${label.toLowerCase()}`}
        >
          <FileArrowDown size={20} weight="bold" /> Abrir PDF — {label}
        </button>
      ) : errored ? (
        <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
          ⚠️ Arquivo não pôde ser exibido como imagem.{" "}
          <button type="button" onClick={() => openAuthedFile(path)} className="underline font-bold">Abrir em nova aba</button>
        </div>
      ) : (
        <a href={url} target="_blank" rel="noreferrer">
          <img
            src={url}
            alt={label}
            className="w-full rounded-lg border border-slate-200"
            onError={() => setErrored(true)}
            data-testid={`kyc-doc-img-${label.toLowerCase()}`}
          />
        </a>
      )}
    </div>
  );
}

function KycTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/kyc/pending").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const review = async (decision) => {
    if (!selected) return;
    if (decision === "reject" && !reason.trim()) return toast.warning("Informe o motivo da rejeição");
    setBusy(true);
    try {
      await api.post(`/admin/kyc/${selected.id}/review`, { decision, reason: reason.trim() });
      toast.success(decision === "approve" ? "Documento aprovado" : "Documento rejeitado");
      setSelected(null); setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-kyc">
      {items.length === 0 ? (
        <div className="aj-card p-8 text-center text-slate-500 text-sm">Nenhum documento aguardando validação.</div>
      ) : (
        <div className="space-y-2">
          {items.map((u) => (
            <button
              key={u.id}
              onClick={() => { setSelected(u); setReason(""); }}
              className="aj-card p-3 w-full text-left hover:bg-slate-50 flex items-center gap-3"
              data-testid={`kyc-row-${u.id}`}
            >
              <IdentificationCard size={22} weight="duotone" className="text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{u.name} <span className="text-[10px] font-mono text-slate-400">{u.support_id}</span></div>
                <div className="text-xs text-slate-500 truncate">
                  {u.email} · {u.cpf ? u.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—"}
                </div>
                <div className="text-xs text-slate-500">
                  Tipo: {(u.kyc_doc_type || "").toUpperCase()} · Nº {u.kyc_doc_number || "—"}
                </div>
              </div>
              <span className="aj-badge bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">Aguardando</span>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setSelected(null)} data-testid="kyc-review-modal">
          <div className="bg-white w-full md:max-w-2xl md:rounded-2xl rounded-t-2xl p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-black text-lg">Validação de Identidade</div>
                <div className="text-xs text-slate-500">{selected.name} · {selected.support_id}</div>
              </div>
              <button onClick={() => setSelected(null)}><X size={20} weight="bold" /></button>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="aj-card p-2"><b>Tipo</b>: {(selected.kyc_doc_type || "").toUpperCase()}</div>
              <div className="aj-card p-2"><b>Número</b>: {selected.kyc_doc_number || "—"}</div>
              <div className="aj-card p-2"><b>CPF</b>: {selected.cpf ? selected.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—"}</div>
              <div className="aj-card p-2"><b>Telefone</b>: {selected.phone || "—"}</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {selected.kyc_doc_front && <KycDocPreview path={selected.kyc_doc_front} label="Frente" />}
              {selected.kyc_doc_back && <KycDocPreview path={selected.kyc_doc_back} label="Verso" />}
            </div>

            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Motivo (obrigatório se rejeitar)"
              className="aj-input"
              data-testid="kyc-rejection-reason"
            />

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => review("reject")} disabled={busy || !reason.trim()} className="px-3 py-2 rounded-full border-2 border-red-200 text-red-700 font-bold disabled:opacity-50" data-testid="kyc-reject-btn">
                Rejeitar
              </button>
              <button onClick={() => review("approve")} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="kyc-approve-btn">
                <CheckCircle size={16} weight="bold" /> Aprovar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// V7.1 — KYC Audit Log Tab
// ============================================================
function KycAuditTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ user_id: "", admin_id: "", action: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_id) params.user_id = filters.user_id.trim();
      if (filters.admin_id) params.admin_id = filters.admin_id.trim();
      if (filters.action) params.action = filters.action;
      const { data } = await api.get("/admin/kyc/audit-log", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { (async () => { await load(); })(); }, []);

  const actionLabel = {
    submit: { text: "Enviou documento", className: "bg-blue-100 text-blue-800" },
    approve: { text: "Aprovou", className: "bg-emerald-100 text-emerald-800" },
    reject: { text: "Rejeitou", className: "bg-red-100 text-red-800" },
  };

  return (
    <div className="space-y-3" data-testid="kyc-audit-tab">
      <div className="aj-card p-3 space-y-2">
        <h3 className="font-heading font-bold text-sm flex items-center gap-1.5">
          <Clock size={16} weight="bold" /> Histórico de Verificações de Identidade
        </h3>
        <p className="text-xs text-slate-500">
          Todos os envios e revisões de identidade. Em ordem cronológica decrescente.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2">
          <input
            placeholder="ID do usuário"
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            className="aj-input"
            data-testid="audit-filter-user-id"
          />
          <input
            placeholder="ID do admin que decidiu"
            value={filters.admin_id}
            onChange={(e) => setFilters({ ...filters, admin_id: e.target.value })}
            className="aj-input"
            data-testid="audit-filter-admin-id"
          />
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="aj-input"
            data-testid="audit-filter-action"
          >
            <option value="">Todas as ações</option>
            <option value="submit">Envios</option>
            <option value="approve">Aprovações</option>
            <option value="reject">Rejeições</option>
          </select>
          <button onClick={load} className="btn-primary text-sm" data-testid="audit-filter-apply">
            <MagnifyingGlass size={14} weight="bold" /> Filtrar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="aj-card p-8 text-center text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="aj-card p-8 text-center text-slate-500">Nenhum registro encontrado.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const meta = actionLabel[it.action] || { text: it.action, className: "bg-slate-100 text-slate-800" };
            const isReject = it.action === "reject";
            return (
              <div key={it.id} className="aj-card p-3 text-sm" data-testid={`audit-row-${it.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.className}`}>
                        {meta.text}
                      </span>
                      {it.doc_type && (
                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          {String(it.doc_type).replace("_", " ")}
                        </span>
                      )}
                      {it.support_id && <span className="text-[10px] font-mono text-slate-500">{it.support_id}</span>}
                    </div>
                    <div className="font-bold mt-1">{it.user_name}</div>
                    <div className="text-xs text-slate-500">{it.user_email}</div>
                    {it.doc_number && <div className="text-xs text-slate-600 mt-0.5">Nº doc: <b>{it.doc_number}</b></div>}
                    {isReject && it.reason && (
                      <div className="mt-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
                        <b>Motivo:</b> {it.reason}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs shrink-0">
                    <div className="text-slate-500">{formatDateTimeBR(it.timestamp)}</div>
                    <div className="mt-1">
                      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Por</div>
                      <div className="font-semibold">{it.by_name || "—"}</div>
                      <div className="text-[10px] text-slate-500">{(it.by_role || "").toUpperCase()}</div>
                    </div>
                  </div>
                </div>
                {it.doc_front && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => openAuthedFile(it.doc_front)}
                      className="text-[11px] text-[#004E98] font-bold underline"
                    >
                      Ver frente do documento
                    </button>
                    {it.doc_back && (
                      <button
                        type="button"
                        onClick={() => openAuthedFile(it.doc_back)}
                        className="text-[11px] text-[#004E98] font-bold underline"
                      >
                        Ver verso
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Gamificação / Recompensas — editor completo (V14.2)
// ============================================================
function IconPicker({ value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {ICON_KEYS.map((k) => (
        <button key={k} type="button" onClick={() => onChange(k)}
          className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${value === k ? "border-[#004E98] bg-[#004E98]/10" : "border-slate-200 bg-white hover:bg-slate-50"}`}
          data-testid={`${testId}-${k}`} title={k}>
          <AchievementIcon icon={k} size={16} className="text-slate-700" />
        </button>
      ))}
    </div>
  );
}

function GamificationTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/gamification");
      setCfg(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setCh = (ci, patch) => setCfg((c) => { const ch = [...c.challenges]; ch[ci] = { ...ch[ci], ...patch }; return { ...c, challenges: ch }; });
  const setMs = (ci, mi, patch) => setCfg((c) => { const ch = [...c.challenges]; const ms = [...ch[ci].milestones]; ms[mi] = { ...ms[mi], ...patch }; ch[ci] = { ...ch[ci], milestones: ms }; return { ...c, challenges: ch }; });
  const addMs = (ci) => setCfg((c) => { const ch = [...c.challenges]; const cur = ch[ci]; ch[ci] = { ...cur, milestones: [...cur.milestones, { count: 1, label: "Novo nível", color: cur.color, reward: 0, icon: "" }] }; return { ...c, challenges: ch }; });
  const removeMs = (ci, mi) => setCfg((c) => { const ch = [...c.challenges]; ch[ci] = { ...ch[ci], milestones: ch[ci].milestones.filter((_, i) => i !== mi) }; return { ...c, challenges: ch }; });
  const addChallenge = () => setCfg((c) => ({ ...c, challenges: [...c.challenges, {
    key: `custom_${Date.now()}`, title: "Novo desafio", enabled: true, metric: "rentals_made",
    icon: "trophy", color: "#FF6B35", unit: "ações", rules: "Descreva como o usuário avança neste desafio.",
    money_reward: false, milestones: [{ count: 1, label: "Nível 1", color: "#94A3B8", reward: 0, icon: "" }],
  }] }));
  const removeChallenge = (ci) => {
    const ch = cfg.challenges[ci];
    if (!window.confirm(`Remover o desafio "${ch.title}"? As medalhas já conquistadas pelos usuários deixarão de aparecer. Esta ação só é aplicada ao salvar.`)) return;
    setCfg((c) => ({ ...c, challenges: c.challenges.filter((_, i) => i !== ci) }));
  };

  const METRIC_OPTIONS = [
    { value: "referral", label: "Amigos indicados (recompensados)" },
    { value: "listings_created", label: "Ferramentas cadastradas" },
    { value: "tools_rented_out", label: "Locações concluídas (como dono)" },
    { value: "rentals_made", label: "Locações concluídas (como locatário)" },
    { value: "freight_count", label: "Nº de fretes/entregas (como dono)" },
    { value: "freight_value", label: "Valor total de frete (R$, como dono)" },
    { value: "total_earned_renting", label: "Total ganho alugando (R$, como dono)" },
    { value: "total_spent_renting", label: "Total gasto alugando (R$, como locatário)" },
    { value: "", label: "Manual / sem progresso automático" },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled, reward: Number(cfg.reward), threshold: Number(cfg.threshold),
        challenges: cfg.challenges.map((ch) => ({
          key: ch.key, title: ch.title, enabled: !!ch.enabled, metric: ch.metric || "", icon: ch.icon, color: ch.color,
          unit: ch.unit, rules: ch.rules, money_reward: !!ch.money_reward,
          milestones: ch.milestones.map((m) => ({ count: Number(m.count) || 0, label: m.label, color: m.color, reward: Number(m.reward) || 0, icon: m.icon || "" })),
        })),
      };
      const { data } = await api.put("/admin/gamification", payload);
      setCfg(data);
      toast.success("Configurações salvas!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-center text-slate-400 py-10" data-testid="gamification-loading">Carregando...</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-gamification">
      {/* Global + indicação */}
      <div className="aj-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift size={20} weight="fill" className="text-[#FF6B35]" />
            <h2 className="font-bold text-lg">Recompensas & Conquistas</h2>
          </div>
          <label className="flex items-center gap-2 text-sm font-bold cursor-pointer" data-testid="gamification-enabled-label">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="w-4 h-4 accent-[#16A34A]" data-testid="gamification-enabled" />
            {cfg.enabled ? "Ativo" : "Inativo"}
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Recompensa por indicação (R$)</label>
            <input type="number" min="0" step="0.5" value={cfg.reward} onChange={(e) => setCfg({ ...cfg, reward: e.target.value })} className="aj-input" data-testid="gamification-reward" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Limiar do convidado (R$)</label>
            <input type="number" min="0" step="10" value={cfg.threshold} onChange={(e) => setCfg({ ...cfg, threshold: e.target.value })} className="aj-input" data-testid="gamification-threshold" />
          </div>
        </div>
        <p className="text-xs text-slate-500">Na indicação, o indicador ganha <b>{formatBRL(Number(cfg.reward) || 0)}</b> quando o convidado completa <b>{formatBRL(Number(cfg.threshold) || 0)}</b> em locações. Cada conquista abaixo pode ser ativada e ter níveis, valor (R$), cor e ícone editados.</p>
      </div>

      {/* Conquistas */}
      {cfg.challenges.map((ch, ci) => (
        <div key={ch.key} className="aj-card p-5 space-y-3" data-testid={`challenge-${ch.key}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: (ch.color || "#FF6B35") + "1A" }}>
              <AchievementIcon icon={ch.icon} size={20} style={{ color: ch.color }} />
            </div>
            <input value={ch.title} onChange={(e) => setCh(ci, { title: e.target.value })} className="aj-input flex-1 font-bold" data-testid={`challenge-title-${ch.key}`} />
            <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer shrink-0">
              <input type="checkbox" checked={!!ch.enabled} onChange={(e) => setCh(ci, { enabled: e.target.checked })} className="w-4 h-4 accent-[#16A34A]" data-testid={`challenge-enabled-${ch.key}`} />
              {ch.enabled ? "Ativa" : "Off"}
            </label>
            <button onClick={() => removeChallenge(ci)} className="text-red-500 shrink-0" title="Remover desafio" data-testid={`challenge-remove-${ch.key}`}>
              <Trash size={18} weight="fill" />
            </button>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-500 mb-1">Métrica de progresso (o que conta neste desafio)</label>
            <select value={ch.metric || ""} onChange={(e) => setCh(ci, { metric: e.target.value })} className="aj-input" data-testid={`challenge-metric-${ch.key}`}>
              {METRIC_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 mb-1">Cor base</label>
              <input type="color" value={ch.color || "#FF6B35"} onChange={(e) => setCh(ci, { color: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200 cursor-pointer" data-testid={`challenge-color-${ch.key}`} />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 mb-1">Unidade (ex: amigos)</label>
              <input value={ch.unit} onChange={(e) => setCh(ci, { unit: e.target.value })} className="aj-input" data-testid={`challenge-unit-${ch.key}`} />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-500 mb-1">Ícone (mesmo para todos os níveis; a cor muda por nível)</label>
            <IconPicker value={ch.icon} onChange={(v) => setCh(ci, { icon: v })} testId={`challenge-icon-${ch.key}`} />
            <input value={/^https?:\/\//.test(ch.icon || "") ? ch.icon : ""} onChange={(e) => setCh(ci, { icon: e.target.value })} placeholder="ou cole a URL de um PNG (opcional)" className="aj-input mt-2 text-xs" data-testid={`challenge-icon-url-${ch.key}`} />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-500 mb-1">Regras (use {"{reward}"} e {"{threshold}"} p/ valores dinâmicos)</label>
            <textarea value={ch.rules} onChange={(e) => setCh(ci, { rules: e.target.value })} rows={2} className="aj-input text-xs" data-testid={`challenge-rules-${ch.key}`} />
          </div>

          <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
            <input type="checkbox" checked={!!ch.money_reward} onChange={(e) => setCh(ci, { money_reward: e.target.checked })} className="w-4 h-4 accent-[#16A34A]" data-testid={`challenge-money-${ch.key}`} />
            Mostrar recompensa em R$ desta conquista
          </label>

          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-500">Níveis</span>
              <button onClick={() => addMs(ci)} className="inline-flex items-center gap-1 text-xs font-bold text-[#004E98]" data-testid={`challenge-add-milestone-${ch.key}`}>
                <PlusCircle size={15} weight="bold" /> Nível
              </button>
            </div>
            {ch.milestones.map((m, mi) => (
              <div key={mi} className="rounded-xl bg-slate-50 p-2.5 space-y-2" data-testid={`milestone-row-${ch.key}-${mi}`}>
                <div className="flex items-center gap-2">
                  <input type="color" value={m.color || ch.color} onChange={(e) => setMs(ci, mi, { color: e.target.value })} className="w-8 h-8 rounded-lg border border-slate-200 shrink-0 cursor-pointer" data-testid={`milestone-color-${ch.key}-${mi}`} />
                  <input value={m.label} onChange={(e) => setMs(ci, mi, { label: e.target.value })} placeholder="Nome do nível" className="aj-input flex-1" data-testid={`milestone-label-${ch.key}-${mi}`} />
                  <button onClick={() => removeMs(ci, mi)} className="text-red-500 shrink-0" data-testid={`milestone-remove-${ch.key}-${mi}`}><MinusCircle size={18} weight="fill" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-0.5">Meta ({ch.unit})</label>
                    <input type="number" min="0" value={m.count} onChange={(e) => setMs(ci, mi, { count: e.target.value })} className="aj-input text-center" data-testid={`milestone-count-${ch.key}-${mi}`} />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-0.5">Prêmio (R$)</label>
                    <input type="number" min="0" step="0.5" value={m.reward} onChange={(e) => setMs(ci, mi, { reward: e.target.value })} className="aj-input text-center" data-testid={`milestone-reward-${ch.key}-${mi}`} />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-400 mb-0.5">Ícone</label>
                    <select value={/^https?:\/\//.test(m.icon || "") ? "" : (m.icon || "")} onChange={(e) => setMs(ci, mi, { icon: e.target.value })} className="aj-input" data-testid={`milestone-icon-${ch.key}-${mi}`}>
                      <option value="">(padrão)</option>
                      {ICON_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <button onClick={addChallenge} className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 font-bold flex items-center justify-center gap-2 hover:border-[#004E98] hover:text-[#004E98] transition-colors" data-testid="gamification-add-challenge">
        <PlusCircle size={18} weight="bold" /> Adicionar minigame
      </button>

      <button onClick={save} disabled={saving} className="w-full py-3 rounded-xl bg-[#004E98] text-white font-bold disabled:opacity-60" data-testid="gamification-save">
        {saving ? "Salvando..." : "Salvar configurações"}
      </button>
    </div>
  );
}


// V17.8 — Passivo da Plataforma: consolida o que os usuários têm em carteira/escrow vs saldo Asaas
function PassivoTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/passivo").then((r) => setData(r.data)).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar passivo");
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-center text-slate-500" data-testid="passivo-loading">Carregando...</div>;
  if (!data) return <div className="p-8 text-center text-slate-500">Sem dados</div>;

  const netCls = data.net_position == null
    ? "bg-slate-100 text-slate-600"
    : data.net_position >= 0 ? "bg-emerald-50 text-emerald-900 border-emerald-300" : "bg-red-50 text-red-900 border-red-300";

  return (
    <div className="space-y-4" data-testid="admin-passivo">
      {/* Passivo TOTAL x Saldo Asaas x Posição líquida */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="aj-card p-4" data-testid="passivo-total-card">
          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Passivo total (a devolver a usuários)</div>
          <div className="font-heading font-black text-3xl text-red-700 mt-1" data-testid="passivo-total-value">{formatBRL(data.total_liability)}</div>
          <div className="text-[11px] text-slate-500 mt-1">carteiras + escrow + saques pendentes</div>
        </div>
        <div className="aj-card p-4" data-testid="passivo-asaas-card">
          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Saldo na Asaas (empresa)</div>
          {data.asaas_balance != null ? (
            <div className="font-heading font-black text-3xl text-[#004E98] mt-1" data-testid="passivo-asaas-value">{formatBRL(data.asaas_balance)}</div>
          ) : (
            <div className="font-heading font-black text-lg text-slate-400 mt-2" data-testid="passivo-asaas-error">indisponível</div>
          )}
          {data.asaas_error && <div className="text-[10px] text-red-600 mt-1 truncate" title={data.asaas_error}>{data.asaas_error}</div>}
        </div>
        <div className={`aj-card p-4 border-2 ${netCls}`} data-testid="passivo-net-card">
          <div className="text-xs font-bold uppercase tracking-wider opacity-70">Posição líquida (Asaas − Passivo)</div>
          {data.net_position != null ? (
            <div className={`font-heading font-black text-3xl mt-1 ${data.net_position >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="passivo-net-value">
              {formatBRL(data.net_position)}
            </div>
          ) : (
            <div className="font-heading font-black text-lg text-slate-400 mt-2">requer saldo Asaas</div>
          )}
          <div className="text-[11px] mt-1 opacity-80">
            {data.net_position != null && (data.net_position >= 0 ? "✓ plataforma cobre todo o passivo" : "⚠ falta liquidez para cobrir o passivo")}
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="aj-card p-4" data-testid="passivo-wallet-breakdown">
          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Carteiras dos usuários</div>
          <div className="text-2xl font-black text-slate-800 mt-1">{formatBRL(data.wallet.total_balance)}</div>
          <div className="text-[11px] text-slate-600 mt-2 space-y-0.5">
            <div>💵 Disponível p/ sacar agora: <b className="text-emerald-700">{formatBRL(data.wallet.total_available)}</b></div>
            <div>🔒 Travado (D+X): <b className="text-amber-700">{formatBRL(data.wallet.total_locked)}</b></div>
            <div>👥 {data.wallet.wallet_count} carteiras · {data.wallet.locks_count} locks</div>
          </div>
        </div>
        <div className="aj-card p-4" data-testid="passivo-escrow-breakdown">
          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Escrow em locações ativas</div>
          <div className="text-2xl font-black text-slate-800 mt-1">{formatBRL(data.escrow.total)}</div>
          <div className="text-[11px] text-slate-600 mt-2 space-y-0.5">
            <div>⏳ Pendente: <b>{formatBRL(data.escrow.pending)}</b></div>
            <div>🛡 Retido (disputa): <b>{formatBRL(data.escrow.held)}</b></div>
            <div>📦 {data.escrow.count} reservas</div>
          </div>
        </div>
        <div className="aj-card p-4" data-testid="passivo-withdrawals-breakdown">
          <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Saques em processamento</div>
          <div className="text-2xl font-black text-slate-800 mt-1">{formatBRL(data.withdrawals_pending.total)}</div>
          <div className="text-[11px] text-slate-600 mt-2">{data.withdrawals_pending.count} saque(s) aguardando PIX out</div>
        </div>
      </div>

      {/* Top 20 maiores carteiras */}
      <div className="aj-card p-4" data-testid="passivo-top-wallets">
        <div className="flex items-center justify-between mb-3">
          <div className="font-black text-[#004E98] flex items-center gap-2"><Wallet size={18} weight="bold" /> Top 20 maiores saldos</div>
          <button onClick={load} className="text-[11px] text-[#004E98] font-bold hover:underline" data-testid="passivo-reload">Atualizar</button>
        </div>
        {data.top_wallets.length === 0 ? (
          <div className="text-slate-400 text-sm text-center py-6">Nenhuma carteira com saldo &gt; 0</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-100">
              <tr>
                <th className="text-left py-2 font-bold">#</th>
                <th className="text-left py-2 font-bold">Usuário</th>
                <th className="text-left py-2 font-bold">E-mail</th>
                <th className="text-right py-2 font-bold">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.top_wallets.map((w, i) => (
                <tr key={w.user_id} className="border-b border-slate-50" data-testid={`passivo-wallet-row-${i}`}>
                  <td className="py-2 text-slate-400 text-xs">{i + 1}</td>
                  <td className="py-2 font-bold text-slate-800">{w.name}</td>
                  <td className="py-2 text-xs text-slate-500 truncate max-w-[240px]">{w.email}</td>
                  <td className="py-2 text-right font-black text-[#004E98]" data-testid={`passivo-wallet-balance-${i}`}>{formatBRL(w.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// V15.x — Central de mensagens: envia push/e-mail para usuários (todos, por cidade ou 1 usuário), agora ou agendado.
function CampaignsTab() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [push, setPush] = useState(true);
  const [email, setEmail] = useState(false);
  const [link, setLink] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [city, setCity] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userMatches, setUserMatches] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  // V17.8 — Recorrência
  const [recurrence, setRecurrence] = useState(""); // "" | "daily" | "weekly" | "monthly"
  const [recurrenceEndAt, setRecurrenceEndAt] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [deviceStats, setDeviceStats] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadList = () => api.get("/admin/campaigns").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    loadList();
    api.get("/admin/push/device-stats").then((r) => setDeviceStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (targetType === "anonymous") { setPush(true); setEmail(false); }
  }, [targetType]);

  useEffect(() => {
    const params = { target_type: targetType };
    if (targetType === "city") { if (!city.trim()) { setEstimate(null); return; } params.city = city.trim(); }
    if (targetType === "users") { if (!selectedUsers.length) { setEstimate(null); return; } params.user_ids = selectedUsers.map((u) => u.id).join(","); }
    const t = setTimeout(() => {
      api.get("/admin/campaigns/estimate", { params }).then((r) => setEstimate(r.data.count)).catch(() => setEstimate(null));
    }, 350);
    return () => clearTimeout(t);
  }, [targetType, city, selectedUsers]);

  const searchUsers = () => {
    if (!userQuery.trim()) return;
    api.get("/admin/users", { params: { q: userQuery.trim(), limit: 8 } }).then((r) => setUserMatches(r.data)).catch(() => setUserMatches([]));
  };

  const addUser = (u) => {
    setSelectedUsers((prev) => prev.some((x) => x.id === u.id) ? prev : [...prev, u]);
    setUserMatches([]); setUserQuery("");
  };
  const removeUser = (id) => setSelectedUsers((prev) => prev.filter((u) => u.id !== id));

  const submit = async () => {
    const channels = [];
    if (push) channels.push("push");
    if (email) channels.push("email");
    if (!channels.length) return toast.warning("Escolha ao menos um canal (push e/ou e-mail).");
    if (!title.trim() || !body.trim()) return toast.warning("Preencha título e mensagem.");
    if (targetType === "city" && !city.trim()) return toast.warning("Informe a cidade.");
    if (targetType === "users" && !selectedUsers.length) return toast.warning("Selecione ao menos um usuário.");
    let schedule_at = null;
    if (scheduleOn) {
      if (!scheduleAt) return toast.warning("Defina a data/hora do agendamento.");
      const dt = new Date(scheduleAt);
      if (isNaN(dt.getTime()) || dt.getTime() < Date.now() - 60000) return toast.warning("A data de agendamento deve ser no futuro.");
      schedule_at = dt.toISOString();
    }
    // V17.8 — recorrência exige agendamento
    if (recurrence && !schedule_at) {
      return toast.warning("Para mensagens recorrentes, ative 'Agendar envio' e escolha a data/hora da primeira execução.");
    }
    let recurrence_end_at = null;
    if (recurrence && recurrenceEndAt) {
      const dtx = new Date(recurrenceEndAt);
      if (isNaN(dtx.getTime())) return toast.warning("Data de término da recorrência inválida.");
      recurrence_end_at = dtx.toISOString();
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/campaigns", {
        title: title.trim(), body: body.trim(), channels, link: link.trim() || null,
        target_type: targetType,
        target_user_ids: targetType === "users" ? selectedUsers.map((u) => u.id) : null,
        target_city: city.trim() || null,
        schedule_at,
        recurrence: recurrence || null,
        recurrence_end_at,
      });
      toast.success(
        recurrence ? "Mensagem recorrente agendada!" :
        data.status === "scheduled" ? "Campanha agendada!" : "Campanha enviada!"
      );
      setTitle(""); setBody(""); setLink(""); setScheduleOn(false); setScheduleAt(""); setSelectedUsers([]);
      setRecurrence(""); setRecurrenceEndAt("");
      loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao criar campanha");
    } finally { setBusy(false); }
  };

  const cancel = async (c) => {
    try { await api.post(`/admin/campaigns/${c.id}/cancel`); toast.success("Campanha cancelada"); loadList(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir a campanha "${c.title}" do histórico?`)) return;
    try { await api.delete(`/admin/campaigns/${c.id}`); toast.success("Campanha excluída"); loadList(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro ao excluir"); }
  };

  const reuse = (c) => {
    setTitle(c.title || ""); setBody(c.body || ""); setLink(c.link || "");
    const ch = c.channels || [];
    setPush(ch.includes("push")); setEmail(ch.includes("email"));
    const t = c.target || {};
    setScheduleOn(false); setScheduleAt("");
    setRecurrence(""); setRecurrenceEndAt("");
    if (t.type === "city") { setTargetType("city"); setCity(t.city || ""); setSelectedUsers([]); }
    else if (t.type === "users" || t.type === "user") {
      setTargetType("users");
      if (Array.isArray(t.users) && t.users.length) {
        setSelectedUsers(t.users.map((u) => ({ id: u.id, name: u.name || "Usuário", email: u.email || u.id })));
      } else {
        const ids = t.user_ids || (t.user_id ? [t.user_id] : []);
        setSelectedUsers(ids.map((id) => ({ id, name: "Usuário", email: id })));
      }
    } else { setTargetType("all"); setCity(""); setSelectedUsers([]); }
    toast.success("Campanha carregada no formulário. Ajuste e envie.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const statusBadge = (s) => {
    const map = {
      sent: ["Enviada", "bg-emerald-100 text-emerald-700"],
      scheduled: ["Agendada", "bg-amber-100 text-amber-700"],
      sending: ["Enviando…", "bg-blue-100 text-blue-700"],
      canceled: ["Cancelada", "bg-slate-200 text-slate-600"],
      failed: ["Falhou", "bg-red-100 text-red-700"],
    };
    const [lbl, cls] = map[s] || [s, "bg-slate-100 text-slate-600"];
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{lbl}</span>;
  };
  const targetLabel = (t) => t?.type === "all" ? "Todos os usuários" : t?.type === "city" ? `Cidade: ${t.city}` : t?.type === "users" ? `${(t.user_ids || []).length} usuário(s)` : "Usuário específico";

  return (
    <div className="space-y-4" data-testid="admin-campaigns">
      {deviceStats && (
        <div className="aj-card p-4 space-y-2" data-testid="admin-device-stats">
          <div className="font-black text-[#004E98] flex items-center gap-2 text-sm">
            <PaperPlaneTilt size={16} weight="bold" /> Dispositivos com push
            {!deviceStats.fcm_enabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">FCM desativado no servidor</span>}
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2.5 py-1 rounded-lg bg-slate-100 font-bold">Total: {deviceStats.total_devices} disp. · {deviceStats.total_users} contas</span>
            {Object.entries(deviceStats.by_platform || {}).map(([plat, n]) => (
              <span key={plat} className={`px-2.5 py-1 rounded-lg font-bold ${plat === "ios" ? "bg-slate-800 text-white" : plat === "android" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`} data-testid={`device-stat-${plat}`}>
                {plat}: {n}
              </span>
            ))}
          </div>
          {(!deviceStats.by_platform || !deviceStats.by_platform.ios) && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
              Nenhum dispositivo <b>iOS</b> registrado ainda. Os tokens aparecem aqui conforme os usuários abrem o app já atualizado e permitem notificações.
            </div>
          )}
          <button
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/push/dedupe-tokens");
                toast.success(`Tokens corrigidos: ${data.tokens_fixed} (${data.rows_deactivated} registros duplicados desativados).`);
                api.get("/admin/push/device-stats").then((r) => setDeviceStats(r.data)).catch(() => {});
              } catch {
                toast.error("Falha ao corrigir tokens.");
              }
            }}
            className="text-[11px] font-bold text-[#004E98] underline"
            data-testid="dedupe-tokens-btn"
          >
            Corrigir entrega cruzada (tokens duplicados)
          </button>
        </div>
      )}

      <div className="aj-card p-4 space-y-3">
        <div className="font-black text-[#004E98] flex items-center gap-2"><PaperPlaneTilt size={18} weight="bold" /> Nova mensagem</div>

        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título (ex: Final de semana chegando!)" className="aj-input" data-testid="campaign-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Mensagem (ex: Aproveite e alugue uma extratora para limpar seus estofados!)" rows={3} className="aj-input resize-none" data-testid="campaign-body" />
        <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Link (opcional) — ex: https://alugaja.app.br/#/itens/123" className="aj-input" data-testid="campaign-link" />

        <div className="flex flex-wrap gap-4 items-center text-sm">
          <span className="font-bold text-slate-600">Canais:</span>
          <label className="flex items-center gap-1.5 cursor-pointer" data-testid="campaign-channel-push">
            <input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} /> Push
          </label>
          {targetType !== "anonymous" && (
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="campaign-channel-email">
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> E-mail
            </label>
          )}
        </div>

        <div className="space-y-2">
          <span className="font-bold text-slate-600 text-sm">Destinatários:</span>
          <select value={targetType} onChange={(e) => { setTargetType(e.target.value); setSelectedUsers([]); setUserMatches([]); }} className="aj-input" data-testid="campaign-target-type">
            <option value="all">Todos os usuários</option>
            <option value="city">Por cidade</option>
            <option value="users">Usuários específicos</option>
            <option value="anonymous">📱 Baixaram o app (sem cadastro)</option>
          </select>
          {targetType === "anonymous" && (
            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="campaign-anonymous-hint">
              Apenas <b>push</b> — dispositivos que instalaram o app e ainda não se cadastraram (sem e-mail). Ideal para incentivar o cadastro.
            </div>
          )}

          {targetType === "city" && (
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Cidade (ex: Sinop)" className="aj-input" data-testid="campaign-city" />
          )}

          {targetType === "users" && (
            <div className="space-y-2">
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="campaign-selected-users">
                  {selectedUsers.map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1.5 bg-[#004E98]/10 text-[#004E98] rounded-full pl-3 pr-1.5 py-1 text-xs font-bold" data-testid={`campaign-user-chip-${u.id}`}>
                      {u.name}
                      <button onClick={() => removeUser(u.id)} className="w-4 h-4 rounded-full bg-[#004E98]/20 hover:bg-red-500 hover:text-white flex items-center justify-center" data-testid={`campaign-user-remove-${u.id}`}>
                        <X size={10} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchUsers()} placeholder="Buscar e adicionar usuário (nome, email, CPF)" className="aj-input" data-testid="campaign-user-search" />
                <button onClick={searchUsers} className="btn-blue px-3" data-testid="campaign-user-search-btn"><MagnifyingGlass size={14} weight="bold" /></button>
              </div>
              {userMatches.map((u) => (
                <button key={u.id} onClick={() => addUser(u)} disabled={selectedUsers.some((x) => x.id === u.id)} className="w-full text-left bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1.5" data-testid={`campaign-user-option-${u.id}`}>
                  <PlusCircle size={15} weight="bold" className="text-[#004E98]" />
                  <span className="font-bold">{u.name}</span> <span className="text-slate-400">· {u.email}</span>
                </button>
              ))}
            </div>
          )}

          {estimate != null && (
            <div className="text-xs text-[#004E98] font-bold" data-testid="campaign-estimate">📣 Alcance estimado: {estimate} {targetType === "anonymous" ? "dispositivo(s)" : "usuário(s)"}</div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-bold text-slate-600 cursor-pointer" data-testid="campaign-schedule-toggle">
            <input type="checkbox" checked={scheduleOn} onChange={(e) => { setScheduleOn(e.target.checked); if (!e.target.checked) setRecurrence(""); }} /> Agendar envio
          </label>
          {scheduleOn && (
            <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="aj-input" data-testid="campaign-schedule-at" />
          )}
          {/* V17.8 — Recorrência (só disponível quando há agendamento) */}
          {scheduleOn && (
            <div className="rounded-lg border-2 border-[#004E98]/20 bg-[#004E98]/[0.03] p-3 space-y-2" data-testid="campaign-recurrence-block">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <ArrowsClockwise size={13} weight="bold" className="text-[#004E98]" /> Recorrência (opcional)
              </label>
              <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} className="aj-input" data-testid="campaign-recurrence">
                <option value="">Envio único</option>
                <option value="daily">Diária (todo dia no mesmo horário)</option>
                <option value="weekly">Semanal (mesmo dia da semana)</option>
                <option value="monthly">Mensal (a cada 30 dias)</option>
              </select>
              {recurrence && (
                <>
                  <label className="text-[11px] font-bold text-slate-600 block">Encerrar recorrência em (opcional — vazio = sem fim)</label>
                  <input type="datetime-local" value={recurrenceEndAt} onChange={(e) => setRecurrenceEndAt(e.target.value)} className="aj-input" data-testid="campaign-recurrence-end" />
                  <div className="text-[10px] text-slate-500">
                    Após cada envio, a próxima execução é agendada automaticamente. Você pode cancelar a série a qualquer momento no histórico.
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <button onClick={submit} disabled={busy} className="btn-blue w-full py-3 font-bold disabled:opacity-50" data-testid="campaign-submit">
          {busy ? "Enviando…" : recurrence ? "Agendar recorrência" : scheduleOn ? "Agendar mensagem" : "Enviar agora"}
        </button>
      </div>

      <div className="space-y-2">
        <button onClick={() => setHistoryOpen((v) => !v)} className="w-full flex items-center justify-between px-1 py-1" data-testid="campaign-history-toggle">
          <span className="font-black text-slate-700 text-sm">Histórico {list.length > 0 && <span className="text-slate-400 font-bold">({list.length})</span>}</span>
          <CaretDown size={18} weight="bold" className={`text-slate-400 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
        </button>
        {historyOpen && (list.length === 0 ? (
          <div className="aj-card p-6 text-center text-slate-400 text-sm" data-testid="campaigns-empty">Nenhuma mensagem enviada ainda.</div>
        ) : list.map((c) => (
          <div key={c.id} className="aj-card p-3" data-testid={`campaign-row-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-bold text-sm truncate">{c.title}</div>
              {statusBadge(c.status)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{c.body}</div>
            <div className="text-[11px] text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              <span>{targetLabel(c.target)}</span>
              <span>Canais: {(c.channels || []).join(", ")}</span>
              {c.scheduled_at && (c.status === "scheduled" || c.recurrence) && <span>⏰ {new Date(c.scheduled_at).toLocaleString("pt-BR")}</span>}
              {c.recurrence && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#004E98]/10 text-[#004E98]" data-testid={`campaign-recurring-${c.id}`}>
                  🔁 {c.recurrence === "daily" ? "Diária" : c.recurrence === "weekly" ? "Semanal" : "Mensal"}
                  {c.recurrence_count ? ` · ${c.recurrence_count}x` : ""}
                </span>
              )}
              {c.stats?.recipients != null && <span>✅ {c.stats.recipients} destinatários (push {c.stats.push_sent || 0} · email {c.stats.email_sent || 0})</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <button onClick={() => reuse(c)} className="text-xs font-bold text-[#004E98] border border-[#004E98]/30 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-testid={`campaign-reuse-${c.id}`}>
                <ArrowsClockwise size={13} weight="bold" /> Reutilizar
              </button>
              {(c.status === "scheduled" || (c.status === "sent" && c.recurrence)) && (
                <button onClick={() => cancel(c)} className="text-xs font-bold text-amber-700 border border-amber-200 rounded-lg px-3 py-1.5" data-testid={`campaign-cancel-${c.id}`}>
                  {c.recurrence ? "Encerrar recorrência" : "Cancelar agendamento"}
                </button>
              )}
              {c.status !== "sending" && (
                <button onClick={() => remove(c)} className="text-xs font-bold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-testid={`campaign-delete-${c.id}`}>
                  <Trash size={13} weight="bold" /> Excluir
                </button>
              )}
            </div>
          </div>
        )))}
      </div>
    </div>
  );
}


// ============================================================================
// V18.9.3 — Painel de Logs de Transações
// ============================================================================

const LOG_STATUS_META = {
  awaiting:  { label: "Aguardando aceite",  chip: "bg-amber-100 text-amber-800 border-amber-200",  dot: "bg-amber-500" },
  running:   { label: "Em andamento",       chip: "bg-blue-100 text-blue-800 border-blue-200",     dot: "bg-blue-500" },
  completed: { label: "Concluída",          chip: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  cancelled: { label: "Recusada/Cancelada", chip: "bg-red-100 text-red-800 border-red-200",        dot: "bg-red-500" },
  noshow:    { label: "No-show",            chip: "bg-orange-100 text-orange-800 border-orange-200", dot: "bg-orange-500" },
  dispute:   { label: "Disputa",            chip: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200", dot: "bg-fuchsia-500" },
  other:     { label: "Outros",             chip: "bg-slate-100 text-slate-700 border-slate-200",   dot: "bg-slate-400" },
};

const LOG_TIMELINE_ICONS = {
  created:   { icon: PlusCircle,     color: "text-slate-500" },
  paid:      { icon: CheckCircle,    color: "text-emerald-600" },
  accepted:  { icon: CheckCircle,    color: "text-blue-600" },
  activated: { icon: Key,            color: "text-emerald-600" },
  returned:  { icon: CheckCircle,    color: "text-emerald-700" },
  noshow:    { icon: Warning,        color: "text-orange-600" },
  cancelled: { icon: X,              color: "text-red-600" },
  autoclose: { icon: Clock,          color: "text-slate-500" },
  refund:    { icon: ArrowCounterClockwise, color: "text-amber-600" },
  audit:     { icon: ClipboardText,  color: "text-slate-400" },
  overdue:   { icon: Warning,        color: "text-red-600" },
  info:      { icon: Lightning,      color: "text-slate-400" },
};

const TIME_CHIPS = [
  { key: "today", label: "Hoje" },
  { key: "week",  label: "Esta semana" },
  { key: "month", label: "Este mês" },
  { key: "custom", label: "Customizado" },
];

const STATUS_CHIPS = [
  { key: "awaiting",  label: "🟡 Aguardando" },
  { key: "running",   label: "🔵 Em andamento" },
  { key: "completed", label: "🟢 Concluída" },
  { key: "cancelled", label: "🔴 Cancelada" },
  { key: "noshow",    label: "🟠 No-Show" },
];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function fmtDateOnly(v) {
  if (!v) return "—";
  // V18.9.17 — o backend manda start_date/end_date como "AAAA-MM-DD". Exibimos DD/MM/AAAA.
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  try {
    return new Date(v).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return String(v);
  }
}

function fmtTimeShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function TransactionLogsTab() {
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [time, setTime] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [city, setCity] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, cities: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("time", time);
    if (time === "custom") {
      if (customStart) params.set("start_date", customStart);
      if (customEnd) params.set("end_date", customEnd);
    }
    if (city) params.set("city", city);
    if (statuses.length) params.set("status", statuses.join(","));
    if (qDebounced) params.set("q", qDebounced);
    params.set("page", String(page));
    params.set("limit", "20");
    setLoading(true);
    api.get(`/admin/logs?${params.toString()}`)
      .then((r) => setData(r.data || { items: [], total: 0, cities: [] }))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar logs"))
      .finally(() => setLoading(false));
  }, [time, customStart, customEnd, city, statuses, qDebounced, page]);

  useEffect(() => { setPage(1); }, [time, customStart, customEnd, city, statuses, qDebounced]);

  const toggleStatus = (s) => {
    setStatuses((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / 20));

  return (
    <div className="space-y-4" data-testid="admin-logs-tab">
      {/* FILTROS FIXOS */}
      <div className="sticky top-0 z-10 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-3" data-testid="logs-filters">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por usuário, item ou código da locação…"
            className="w-full pl-10 pr-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
            data-testid="logs-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="logs-time-chips">
          {TIME_CHIPS.map((t) => (
            <button key={t.key} onClick={() => setTime(t.key)}
              className={`px-3 py-1 text-xs font-bold rounded-full border transition ${time === t.key ? "bg-[#004E98] text-white border-[#004E98]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}
              data-testid={`logs-time-${t.key}`}>
              {t.label}
            </button>
          ))}
          {time === "custom" && (
            <div className="flex items-center gap-1 ml-2 text-xs">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                className="px-2 py-1 border border-slate-300 rounded" data-testid="logs-custom-start" />
              <span className="text-slate-500">até</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2 py-1 border border-slate-300 rounded" data-testid="logs-custom-end" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={city} onChange={(e) => setCity(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm"
            data-testid="logs-city-select">
            <option value="">Todas as cidades</option>
            {(data.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex flex-wrap gap-1.5" data-testid="logs-status-chips">
            {STATUS_CHIPS.map((s) => (
              <button key={s.key} onClick={() => toggleStatus(s.key)}
                className={`px-2.5 py-1 text-xs font-bold rounded-full border transition ${statuses.includes(s.key) ? "bg-[#004E98] text-white border-[#004E98]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}
                data-testid={`logs-status-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="ml-auto text-xs text-slate-500" data-testid="logs-count">
            {loading ? "Carregando…" : `${data.total || 0} resultado(s)`}
          </div>
        </div>
      </div>

      {/* LISTA DE CARDS */}
      <div className="space-y-2.5">
        {(!loading && (data.items || []).length === 0) && (
          <div className="text-center py-8 text-slate-500 text-sm" data-testid="logs-empty">
            Nenhuma transação encontrada com esses filtros.
          </div>
        )}
        {(data.items || []).map((it) => {
          const meta = LOG_STATUS_META[it.status_bucket] || LOG_STATUS_META.other;
          const isOpen = !!expanded[it.id];
          return (
            <div key={it.id} className={`rounded-xl border-2 bg-white transition ${isOpen ? "border-[#004E98]/40 shadow-sm" : "border-slate-200"}`} data-testid={`log-card-${it.id}`}>
              <button type="button" onClick={() => setExpanded((m) => ({ ...m, [it.id]: !isOpen }))}
                className="w-full text-left p-3 flex flex-col gap-1.5" data-testid={`log-card-${it.id}-toggle`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-black uppercase tracking-wider ${meta.chip}`} data-testid={`log-card-${it.id}-status`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                    {meta.label}
                  </span>
                  <span className="text-[11px] text-slate-500 font-medium">{fmtDateTime(it.created_at)}</span>
                </div>
                {it.return_overdue?.overdue && (
                  <div className="flex items-center gap-1.5 text-[11px] font-black text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1" data-testid={`log-card-${it.id}-overdue`}>
                    <Warning size={13} weight="fill" />
                    DEVOLUÇÃO ATRASADA — venceu {fmtDateTime(it.return_overdue.due_at)} ({it.return_overdue.hours}h)
                  </div>
                )}
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-800 truncate">
                      <span className="text-slate-500">{it.renter?.name || "—"}</span>
                      <span className="text-slate-400"> alugou de </span>
                      <span className="text-slate-700">{it.owner?.name || "—"}</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      <b>{it.listing?.title || "—"}</b>
                      {(it.listing?.city || it.listing?.uf) && <> · {it.listing?.city}{it.listing?.uf ? ` - ${it.listing.uf}` : ""}</>}
                      {it.quantity > 1 && <> · qty {it.quantity}</>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-black text-[#004E98]">{formatBRL(it.totals?.total_charged || 0)}</div>
                    <div className="text-[10px] text-slate-500 flex items-center justify-end gap-1">
                      {isOpen ? "Fechar" : "Ver detalhes"}
                      {isOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                    </div>
                  </div>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-slate-100 p-3 space-y-4" data-testid={`log-card-${it.id}-details`}>
                  <section data-testid={`log-card-${it.id}-payment`}>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-slate-600 mb-1.5">
                      <Coins size={14} weight="bold" className="text-[#004E98]" /> Fluxo de pagamentos
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div className="text-slate-500">Método</div>
                      <div className="text-right font-bold uppercase">{it.payment?.method || "—"}</div>
                      <div className="text-slate-500">Status</div>
                      <div className="text-right font-bold">{it.payment?.payment_status || "—"}{it.payment?.charge_released ? " · liberado" : ""}</div>
                      <div className="text-slate-500">Locatário pagou</div>
                      <div className="text-right font-bold">{formatBRL(it.totals?.total_charged || 0)}</div>
                      {it.totals?.discount_amount > 0 && (
                        <>
                          <div className="text-slate-500">Desconto aplicado</div>
                          <div className="text-right text-emerald-700">-{formatBRL(it.totals.discount_amount)}{it.totals.discount_label ? ` · ${it.totals.discount_label}` : ""}</div>
                        </>
                      )}
                      {it.totals?.valor_frete > 0 && (
                        <>
                          <div className="text-slate-500">Frete</div>
                          <div className="text-right">{formatBRL(it.totals.valor_frete)}</div>
                        </>
                      )}
                      {it.totals?.caucao_amount > 0 && (
                        <>
                          <div className="text-slate-500">Caução</div>
                          <div className="text-right">{formatBRL(it.totals.caucao_amount)}</div>
                        </>
                      )}
                      <div className="text-slate-500">Taxa AlugaJá</div>
                      <div className="text-right text-red-600">-{formatBRL((it.totals?.locador_fee || 0) + (it.totals?.locatario_fee || 0))}</div>
                      <div className="text-slate-500">Dono recebeu (líquido)</div>
                      <div className="text-right font-bold text-emerald-700">{formatBRL(it.totals?.owner_net || 0)}</div>
                      {it.totals?.owner_net_original != null && it.totals.owner_net_original !== it.totals.owner_net && (
                        <>
                          <div className="text-slate-500 text-[10px]">Owner net original</div>
                          <div className="text-right text-[10px] text-slate-500 line-through">{formatBRL(it.totals.owner_net_original)}</div>
                        </>
                      )}
                      {it.payment?.refunded_at && (
                        <>
                          <div className="text-slate-500">Estorno em</div>
                          <div className="text-right text-amber-700">{fmtDateTime(it.payment.refunded_at)}</div>
                        </>
                      )}
                    </div>
                  </section>

                  <section data-testid={`log-card-${it.id}-pickup`}>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-slate-600 mb-1.5">
                      <Key size={14} weight="bold" className="text-[#004E98]" /> Fluxo de retirada
                    </div>
                    <div className="text-xs space-y-1">
                      {it.pickup?.token_validated_at ? (
                        <div className="text-emerald-700 font-bold">✅ Código inserido às {fmtDateTime(it.pickup.token_validated_at)} · locação ativada</div>
                      ) : it.pickup?.no_show_claimed_at ? (
                        <div className="text-orange-700 font-bold">
                          ⚠️ Reclamação de no-show aberta por <b>{it.pickup?.no_show_claimed_by || "usuário"}</b> em {fmtDateTime(it.pickup.no_show_claimed_at)}
                        </div>
                      ) : it.status_bucket === "cancelled" ? (
                        <div className="text-red-700 font-bold">❌ Locação encerrada sem retirada · {it.cancel_reason || "cancelada"}</div>
                      ) : (
                        <div className="text-slate-600">Código: <span className="font-mono font-black text-slate-800">{it.pickup?.token_retirada || "—"}</span> · aguardando validação</div>
                      )}
                      <div className="text-slate-500 text-[11px]">
                        Retirada prevista: {fmtDateOnly(it.start_date)}{it.start_time ? ` às ${it.start_time}` : ""} · Devolução: {fmtDateOnly(it.end_date)}{it.end_time ? ` às ${it.end_time}` : ""}
                      </div>
                      {it.return_overdue?.overdue && (
                        <div className="text-red-700 font-bold text-[11px]" data-testid={`log-card-${it.id}-overdue-detail`}>
                          ⏰ Prazo de devolução vencido há <b>{it.return_overdue.hours}h</b> — item ainda não devolvido.
                        </div>
                      )}
                    </div>
                  </section>

                  <section data-testid={`log-card-${it.id}-timeline`}>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-slate-600 mb-1.5">
                      <Timer size={14} weight="bold" className="text-[#004E98]" /> Histórico
                    </div>
                    <ol className="space-y-1.5">
                      {(it.timeline || []).map((ev, idx) => {
                        const cfg = LOG_TIMELINE_ICONS[ev.type] || LOG_TIMELINE_ICONS.info;
                        const Icon = cfg.icon;
                        return (
                          <li key={idx} className="flex items-start gap-2 text-xs" data-testid={`log-card-${it.id}-tl-${idx}`}>
                            <Icon size={13} weight="bold" className={`shrink-0 mt-0.5 ${cfg.color}`} />
                            <span className="text-slate-500 font-mono text-[10px] shrink-0">{fmtTimeShort(ev.ts)}</span>
                            <span className="text-slate-700 flex-1 break-words">{ev.event}</span>
                          </li>
                        );
                      })}
                      {(it.timeline || []).length === 0 && (
                        <li className="text-[11px] text-slate-400">Sem eventos registrados.</li>
                      )}
                    </ol>
                  </section>

                  <details className="text-[11px] text-slate-500" data-testid={`log-card-${it.id}-metadata`}>
                    <summary className="cursor-pointer font-bold">IDs e metadata</summary>
                    <div className="mt-1 space-y-0.5 font-mono">
                      <div>booking_id: <b>{it.id}</b></div>
                      <div>listing_id: {it.listing?.id}</div>
                      <div>gateway: {it.payment?.gateway || "—"} · session: {it.payment?.session_id || "—"}</div>
                      <div>asaas_payment_id: {it.payment?.asaas_payment_id || "—"}</div>
                    </div>
                  </details>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2" data-testid="logs-pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="px-3 py-1 rounded-lg border border-slate-300 text-sm font-bold disabled:opacity-40" data-testid="logs-prev">
            ← Anterior
          </button>
          <span className="text-xs text-slate-600 font-medium">Página {page} de {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="px-3 py-1 rounded-lg border border-slate-300 text-sm font-bold disabled:opacity-40" data-testid="logs-next">
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
}
